// ManDana inline databases. Data sources, rows and views are separate sync
// documents; this module owns their schema, derived values and mutations.

type MdDatabasePropertyType = 'title' | 'text' | 'number' | 'select' | 'multi' |
  'status' | 'date' | 'checkbox' | 'url' | 'email' | 'phone' | 'person' |
  'files' | 'formula' | 'relation' | 'rollup' | 'button' | 'id' | 'place' |
  'created_time' | 'created_by' | 'updated_time' | 'updated_by';
type MdDatabaseViewType = 'table' | 'board' | 'timeline' | 'calendar' | 'list' |
  'gallery' | 'chart' | 'dashboard' | 'feed' | 'map' | 'form' | 'schedule';
type MdDatabaseChartAggregation = 'count' | 'sum' | 'average' | 'min' | 'max';
type MdDatabaseCalculation = 'none' | 'count' | 'count_values' | 'count_unique' |
  'percent_empty' | 'percent_not_empty' | 'percent_checked' | 'percent_unchecked' |
  'sum' | 'average' | 'median' | 'min' | 'max' | 'range' | 'earliest' | 'latest' |
  'date_range';

interface MdDatabaseOption { id: string; name: string; color: string; }
interface MdDatabaseFile { id: string; name: string; url?: string; mime?: string; size?: number; }
interface MdDatabaseProperty {
  id: string;
  name: string;
  type: MdDatabasePropertyType;
  options?: MdDatabaseOption[];
  format?: string;
  formula?: string;
  relation?: { databaseId: string; limit: 'one' | 'many'; reciprocalPropertyId?: string };
  rollup?: { relationPropertyId: string; targetPropertyId: string; calculation: MdDatabaseCalculation | 'show_original' };
  button?: { label: string; action: 'open_row' | 'set_value' | 'add_row' | 'open_url'; targetPropertyId?: string; value?: unknown; url?: string };
}
interface MdDatabaseRow {
  id: string;
  values: Record<string, unknown>;
  body: string;
  /** Present on a row composed from a membership entry: the ref key of the
      record it presents. Composed at view time, never stored in db.rows and
      never offered to sync as a row document. See mdDatabaseRowsForView. */
  member?: string;
  /** Present on a row nothing can be written to: the values are composed from
      a source this database does not own, so there is no cell to store an edit
      in. The table draws it as text rather than as inputs that could only
      refuse (md-database.jsx's DbCell). Omana People's roster is the case. */
  readOnly?: boolean;
  icon?: string;
  cover?: string;
  favorite?: boolean;
  comments?: Array<{ id: string; author: string; body: string; created: number }>;
  created: number;
  updated: number;
  createdBy?: string;
  updatedBy?: string;
}
interface MdDatabaseSort { propertyId: string; direction: 'asc' | 'desc'; }
interface MdDatabaseFilter {
  propertyId: string;
  operator: 'is' | 'is_not' | 'contains' | 'not_contains' | 'starts_with' |
    'ends_with' | 'greater' | 'greater_equal' | 'less' | 'less_equal' | 'before' |
    'after' | 'on_or_before' | 'on_or_after' | 'checked' | 'unchecked' |
    'not_empty' | 'empty';
  value?: unknown;
}
interface MdDatabaseView {
  id: string;
  name: string;
  type: MdDatabaseViewType;
  visiblePropertyIds: string[];
  sorts: MdDatabaseSort[];
  filters: MdDatabaseFilter[];
  groupBy?: string | null;
  groupDirection?: 'asc' | 'desc';
  hiddenGroups?: string[];
  calculations?: Record<string, MdDatabaseCalculation>;
  datePropertyId?: string | null;
  endDatePropertyId?: string | null;
  timelineZoom?: 'day' | 'week' | 'month' | 'quarter';
  chartPropertyId?: string | null;
  chartType?: 'bar' | 'donut';
  chartAggregation?: MdDatabaseChartAggregation;
  propertyWidths?: Record<string, number>;
  frozenPropertyIds?: string[];
  wrapPropertyIds?: string[];
  showDataSourceTitle?: boolean;
  showVerticalLines?: boolean;
  showPageIcon?: boolean;
  wrapContent?: boolean;
  openPagesIn?: 'side_peek' | 'center_peek' | 'full_page';
  conditionalColors?: Array<{ id: string; propertyId: string; operator: MdDatabaseFilter['operator']; value?: unknown; color: string }>;
  dashboardAggregations?: Record<string, 'sum' | 'average' | 'median' | 'count'>;
}
interface MdDatabaseTemplate { id: string; name: string; icon?: string; body: string; values: Record<string, unknown>; }
interface MdDatabaseAutomation {
  id: string;
  name: string;
  enabled: boolean;
  trigger: 'row_added' | 'property_changed';
  triggerPropertyId?: string;
  action: 'set_property' | 'add_row';
  actionPropertyId?: string;
  value?: unknown;
}
interface MdDatabase {
  version: 3;
  id: string;
  name: string;
  /** What contains this library, as a ref key. Absent means the root. */
  scopeRef?: string;
  properties: MdDatabaseProperty[];
  rows: MdDatabaseRow[];
  views: MdDatabaseView[];
  activeViewId: string;
  locked?: boolean;
  templates?: MdDatabaseTemplate[];
  automations?: MdDatabaseAutomation[];
  created: number;
  updated: number;
  revision: number;
}
/* Phase 1C: a view is its own document (md:view:<id> locally, view:<id> on
   the wire) with its own revision, so presentation edits and schema edits
   never revise the same sync document. The database document carries only an
   ordered list of view ids. The composed MdDatabase the UI consumes is
   unchanged — hydration happens at the read/apply/save boundary. */
interface MdViewDoc extends MdDatabaseView { version: 1; databaseId: string; updated: number; revision: number; }
/* Phase 1C-2: a form is a schema-bound INPUT CONTRACT over one library — it
   names the fields it accepts, the library they land in, and the single
   command a valid submission runs. It is not a Manifestation and it is not a
   permission: holding a form does not confer arbitrary writes to its
   destination, because submission reads only the declared fields. Like a
   view, a form is its own sync document (md:form:<id> locally, form:<id> on
   the wire) with its own revision. */
interface MdFormField {
  propertyId: string;
  label?: string;
  help?: string;
  required?: boolean;
  default?: unknown;
}
interface MdFormDoc {
  version: 1;
  id: string;
  databaseId: string;
  name: string;
  intro?: string;
  fields: MdFormField[];
  command: 'database.row.add';
  updated: number;
  revision: number;
}
type MdFormSubmitReason = 'missing_form' | 'missing_database' | 'locked' | 'invalid' | 'storage_full';
interface MdFormFieldError { propertyId: string; reason: 'unknown_field' | 'not_writable' | 'required' | 'invalid'; }
type MdFormSubmitResult =
  | { ok: true; formId: string; databaseId: string; rowId: string; values: Record<string, unknown> }
  | { ok: false; reason: MdFormSubmitReason; errors: MdFormFieldError[] };
type MdDatabaseMetaDoc = Omit<MdDatabase, 'rows' | 'views'> & { rows: []; views: string[] };
type MdDatabaseMutationReason = 'storage_full' | 'invalid' | 'missing' | 'locked';
type MdDatabaseMutationResult = { ok: true; value: MdDatabase; changed: boolean } |
  { ok: false; reason: MdDatabaseMutationReason };
interface MdDatabaseCellEdit { rowId: string; propertyId: string; value: unknown; }

(function (g: any) {
  'use strict';

  const VERSION = 3 as const;
  const ID_RE = /^[a-z0-9]{6,40}$/;
  const LINE_RE = /^!\[Database\]\(mddb:([a-z0-9]{6,40})\)$/;
  const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/;
  /* A date is a day the calendar can actually produce, not a string in the
     shape of one. DAY_RE answers the shape; this answers whether the year meant
     it. Without the second question 2026-02-31 was stored verbatim and then
     sorted, filtered, ranged by dateBetween and drawn on a timeline as though
     it were a day — and February 31st sorts perfectly happily between the 30th
     and March 1st, which is exactly why nothing complained.

     Date is the arbiter rather than a table of month lengths, because it already
     knows every leap rule there is: Date.UTC rolls an impossible day forward
     (Feb 31 becomes Mar 3), so a value that does not survive the round trip was
     never a date. The clock is checked by range instead — Date has no opinion
     about 24:00 once the day has already rolled. */
  function realDay(value: unknown): boolean {
    if (typeof value !== 'string') return false;
    const m = DAY_RE.exec(value);
    if (!m) return false;
    const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
    if (Number(m[4] ?? 0) > 23 || Number(m[5] ?? 0) > 59 || Number(m[6] ?? 0) > 59) return false;
    const probe = new Date(Date.UTC(year, month - 1, day));
    return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1
      && probe.getUTCDate() === day;
  }
  const TYPES: MdDatabasePropertyType[] = ['title', 'text', 'number', 'select',
    'multi', 'status', 'date', 'checkbox', 'url', 'email', 'phone', 'person',
    'files', 'formula', 'relation', 'rollup', 'button', 'id', 'place',
    'created_time', 'created_by', 'updated_time', 'updated_by'];
  const VIEW_TYPES: MdDatabaseViewType[] = ['table', 'board', 'gallery', 'list',
    'chart', 'dashboard', 'timeline', 'feed', 'map', 'calendar', 'form', 'schedule'];
  const FILTERS = ['is', 'is_not', 'contains', 'not_contains', 'starts_with',
    'ends_with', 'greater', 'greater_equal', 'less', 'less_equal', 'before',
    'after', 'on_or_before', 'on_or_after', 'checked', 'unchecked', 'not_empty',
    'empty'] as const;
  const CALCULATIONS: MdDatabaseCalculation[] = ['none', 'count', 'count_values',
    'count_unique', 'percent_empty', 'percent_not_empty', 'percent_checked',
    'percent_unchecked', 'sum', 'average', 'median', 'min', 'max', 'range',
    'earliest', 'latest', 'date_range'];
  const COLORS = ['violet', 'mint', 'blue', 'rose', 'amber', 'coral', 'cyan'];
  const COMPUTED = new Set<MdDatabasePropertyType>(['formula', 'rollup', 'button',
    'id', 'created_time', 'created_by', 'updated_time', 'updated_by']);
  const MAX_PROPERTIES = 80;
  const MAX_ROWS = 5000;
  const MAX_VIEWS = 32;
  const CHART_AGGREGATIONS: MdDatabaseChartAggregation[] = ['count', 'sum', 'average', 'min', 'max'];
  const DASHBOARD_AGGREGATIONS = ['sum', 'average', 'median', 'count'];

  let seq = 0;
  /* One undo stack holds EVERY home a database's data lives in: the database
     document, its membership document, and — since the Main DB — the record's
     own home, which is not this database's document at all. Ctrl+Z is a promise
     about everything the person just did here, and they cannot see those seams.

     A `record` item holds the value rather than a snapshot, because there is no
     snapshot to hold: the write went to a day document, a project index or a
     board's scope, and each of those has its own door. The value is in
     mdLibrarianCellSet's vocabulary, so undo is that same door called again. */
  type MdDatabaseHistoryItem =
    | { kind: 'db'; value: MdDatabase }
    | { kind: 'membership'; value: unknown[] }
    | { kind: 'both'; db: MdDatabase; membership: unknown[] }
    | { kind: 'record'; key: string; propertyId: string; value: unknown };
  const historyByDatabase = new Map<string, { undo: MdDatabaseHistoryItem[]; redo: MdDatabaseHistoryItem[] }>();
  /* Reached through a wrapper rather than used as a bare Map, so that the row
     index below cannot go stale. Twelve call sites invalidate this cache — a
     save, a delete, a row document arriving from sync, an import — and every
     one of them has to invalidate the row index too. Routing the mutations
     through here is what makes that true by construction instead of by twelve
     people remembering. Only get/set/delete/clear are used; nothing iterates. */
  const canonicalStore = new Map<string, MdDatabase>();
  let rowIndex: Map<string, { databaseId: string; row: MdDatabaseRow }> | null = null;
  const canonicalByDatabase = {
    get: (key: string) => canonicalStore.get(key),
    has: (key: string) => canonicalStore.has(key),
    set: (key: string, value: MdDatabase) => { rowIndex = null; canonicalStore.set(key, value); },
    delete: (key: string) => { rowIndex = null; return canonicalStore.delete(key); },
    clear: () => { rowIndex = null; canonicalStore.clear(); },
  };
  const canonicalDatabases = new WeakSet<object>();
  const HISTORY_LIMIT = 30;
  function id(prefix = ''): string {
    return prefix + Date.now().toString(36) + (seq++).toString(36)
      + Math.random().toString(36).slice(2, 7);
  }
  /* The fallback id a SANITISER may invent. Sanitising is a function: the same
     document must sanitise to the same database on every device and on every
     apply, or sync never converges — an empty `views` list once drew its
     default view's id from `id()` above, and two tabs re-applying the same
     document pushed a fresh difference at each other four times a second
     (staging, 2026-09-01). So an invented id is derived from where the item
     stands — its anchor document and its position — never from the clock or
     the dice. A collision with a real id is possible in principle; every list
     that mints one already deduplicates through a seen-set. */
  const derivedId = (prefix: string, anchor: string, index: number): string =>
    (prefix + index.toString(36) + anchor).slice(0, 40);
  const text = (value: unknown, max = 120): string =>
    typeof value === 'string' ? value.trim().slice(0, max) : '';
  const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
  const validId = (value: unknown): value is string => typeof value === 'string' && ID_RE.test(value);
  /* The Main DB, asked for by late binding rather than by a constant copied
     into this file: md-librarian.ts loads after this module and owns the id.
     Undefined until it does, so a build without it behaves exactly as before. */
  const isLibrarian = (dbId: unknown): boolean => !!g.MD_LIBRARIAN_ID && dbId === g.MD_LIBRARIAN_ID;
  /* Omana People, asked for the same way and for the same reason. The rows here
     are a projection of a table on the server rather than of this account's own
     records — see app/md-omana.ts. */
  const isOmana = (dbId: unknown): boolean => !!g.MD_OMANA_ID && dbId === g.MD_OMANA_ID;
  /* What the two have in common, which is everything this file cares about: a
     database ManDana SHIPS. Its definition is code, its rows are composed on
     read and never stored, it cannot be deleted and no row can be added to it.
     Every refusal below asks this rather than naming one of them, so the third
     one costs a constant and not a search through this file. */
  const isShipped = (dbId: unknown): boolean => isLibrarian(dbId) || isOmana(dbId);
  /* The shipped definition, whichever ships it. Late-bound, so a build without
     either module behaves exactly as it did before there was one. */
  const shippedDatabase = (dbId: unknown): unknown =>
    (g.mdLibrarianDatabase && g.mdLibrarianDatabase(dbId))
    || (g.mdOmanaDatabase && g.mdOmanaDatabase(dbId))
    || null;
  const finite = (value: unknown, fallback: number): number => Number.isFinite(value) ? Number(value) : fallback;
  const isEmpty = (value: unknown): boolean => value == null || value === '' ||
    (Array.isArray(value) && value.length === 0);

  function options(value: unknown, anchor: string): MdDatabaseOption[] {
    if (!Array.isArray(value)) return [];
    const out: MdDatabaseOption[] = [], seen = new Set<string>();
    for (const [at, raw] of value.slice(0, 120).entries()) {
      if (!raw || typeof raw !== 'object') continue;
      const rid = validId((raw as any).id) ? (raw as any).id : derivedId('o', anchor, at);
      const name = text((raw as any).name, 80);
      if (!name || seen.has(rid)) continue;
      seen.add(rid);
      const color = COLORS.includes((raw as any).color) ? (raw as any).color : COLORS[out.length % COLORS.length]!;
      out.push({ id: rid, name, color });
    }
    return out;
  }

  function property(rawValue: any, fallbackId?: string): MdDatabaseProperty | null {
    if (!rawValue || typeof rawValue !== 'object') return null;
    const type = TYPES.includes(rawValue.type) ? rawValue.type as MdDatabasePropertyType : null;
    const pid = rawValue.id === 'title' ? 'title' : validId(rawValue.id) ? rawValue.id : fallbackId || id('p');
    if (!type) return null;
    const out: MdDatabaseProperty = { id: pid, name: text(rawValue.name, 80) || 'Property', type };
    if (['select', 'multi', 'status'].includes(type)) out.options = options(rawValue.options, pid);
    if (type === 'number') out.format = text(rawValue.format, 30) || 'number';
    if (type === 'formula') out.formula = typeof rawValue.formula === 'string' ? rawValue.formula.slice(0, 4000) : '';
    if (type === 'relation' && rawValue.relation && validId(rawValue.relation.databaseId)) {
      out.relation = { databaseId: rawValue.relation.databaseId,
        limit: rawValue.relation.limit === 'one' ? 'one' : 'many' };
      if (validId(rawValue.relation.reciprocalPropertyId)) out.relation.reciprocalPropertyId = rawValue.relation.reciprocalPropertyId;
    }
    if (type === 'rollup' && rawValue.rollup && validId(rawValue.rollup.relationPropertyId)
      && typeof rawValue.rollup.targetPropertyId === 'string') {
      const calculation = [...CALCULATIONS, 'show_original'].includes(rawValue.rollup.calculation)
        ? rawValue.rollup.calculation : 'show_original';
      out.rollup = { relationPropertyId: rawValue.rollup.relationPropertyId,
        targetPropertyId: rawValue.rollup.targetPropertyId, calculation };
    }
    if (type === 'button') {
      const b = rawValue.button || {};
      const action = ['open_row', 'set_value', 'add_row', 'open_url'].includes(b.action) ? b.action : 'open_row';
      out.button = { label: text(b.label, 50) || 'Run', action };
      if (typeof b.targetPropertyId === 'string') out.button.targetPropertyId = b.targetPropertyId;
      if ('value' in b) out.button.value = clone(b.value);
      if (typeof b.url === 'string') out.button.url = b.url.slice(0, 2000);
    }
    return out;
  }

  function properties(value: unknown, anchor: string): MdDatabaseProperty[] {
    const raw = Array.isArray(value) ? value : [];
    const out: MdDatabaseProperty[] = [], seen = new Set<string>();
    for (const [at, item] of raw.slice(0, MAX_PROPERTIES).entries()) {
      const prop = property(item, derivedId('p', anchor, at));
      if (!prop || seen.has(prop.id) || (prop.type === 'title' && out.some((p) => p.type === 'title'))) continue;
      seen.add(prop.id); out.push(prop);
    }
    const titleAt = out.findIndex((p) => p.type === 'title');
    if (titleAt < 0) out.unshift({ id: 'title', name: 'Name', type: 'title' });
    else if (titleAt > 0) out.unshift(out.splice(titleAt, 1)[0]!);
    out[0]!.id = 'title'; out[0]!.type = 'title';
    return out.slice(0, MAX_PROPERTIES);
  }

  function cleanFiles(value: unknown): MdDatabaseFile[] {
    if (!Array.isArray(value)) return [];
    return value.slice(0, 40).flatMap((raw: any, at: number) => {
      if (!raw || typeof raw !== 'object' || (!validId(raw.id) && typeof raw.url !== 'string')) return [];
      return [{ id: validId(raw.id) ? raw.id
          : derivedId('f', String(raw.url).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 32) || 'nameless', at),
        name: text(raw.name, 180) || 'File',
        url: typeof raw.url === 'string' ? raw.url.slice(0, 4000) : undefined,
        mime: text(raw.mime || raw.type, 100), size: finite(raw.size, 0) }];
    });
  }

  function cleanValue(prop: MdDatabaseProperty, value: unknown): unknown {
    if (COMPUTED.has(prop.type)) return null;
    if (prop.type === 'checkbox') return !!value;
    if (prop.type === 'number') return value === '' || value == null ? null
      : Number.isFinite(Number(value)) ? Number(value) : null;
    if (prop.type === 'date') return realDay(value) ? value as string : null;
    if (prop.type === 'select' || prop.type === 'status') return typeof value === 'string'
      && (prop.options || []).some((o) => o.id === value) ? value : null;
    if (prop.type === 'multi') {
      const allowed = new Set((prop.options || []).map((o) => o.id));
      return Array.isArray(value) ? [...new Set(value.filter((v) => typeof v === 'string' && allowed.has(v)))] : [];
    }
    if (prop.type === 'person') return Array.isArray(value)
      ? [...new Set(value.filter((v) => typeof v === 'string').map((v) => v.trim()).filter(Boolean))].slice(0, 30) : [];
    if (prop.type === 'files') return cleanFiles(value);
    if (prop.type === 'relation') {
      const ids = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
      const clean = [...new Set(ids.filter(validId))];
      return prop.relation?.limit === 'one' ? clean.slice(0, 1) : clean.slice(0, 500);
    }
    return typeof value === 'string' ? value.slice(0, 10000) : '';
  }

  /** Did the value the caller passed survive cleaning, or did it vanish?

      cleanValue cannot answer this itself, and must not try: it is the READ
      path's sanitiser too, where dropping a value it cannot parse is exactly
      right — a half-written cell should read as empty and never take the
      surface down with it. The guarded write door wants the opposite, because
      `ok: true` there is a promise that what you passed is what is stored. So
      the question is asked here, by the door, and the sanitiser stays forgiving.

      CLEARING IS NOT FAILING. null, the empty string and the empty array are
      things people do on purpose, and every one of them succeeds. Neither is
      NORMALISING: "12" for a number cell is the same number the caller meant,
      and a checkbox has a truthiness for every value there is. What is left —
      a word for a number, an option that does not exist, a day the calendar
      cannot produce — is a value that could not be stored, and saying so is
      the entire point of a door that returns typed refusals. */
  function representable(prop: MdDatabaseProperty, given: unknown, cleaned: unknown): boolean {
    if (COMPUTED.has(prop.type) || prop.type === 'checkbox') return true;
    if (given == null || given === '' || (Array.isArray(given) && given.length === 0)) return true;
    if (cleaned == null || cleaned === '') return false;
    return !(Array.isArray(cleaned) && cleaned.length === 0);
  }

  function rows(value: unknown, props: MdDatabaseProperty[], anchor: string): MdDatabaseRow[] {
    if (!Array.isArray(value)) return [];
    const out: MdDatabaseRow[] = [], seen = new Set<string>(), byId = new Map(props.map((p) => [p.id, p]));
    for (const [at, item] of value.slice(0, MAX_ROWS).entries()) {
      if (!item || typeof item !== 'object') continue;
      const raw = item as any, rid = validId(raw.id) ? raw.id : derivedId('r', anchor, at);
      if (seen.has(rid)) continue;
      seen.add(rid);
      const incoming = raw.values && typeof raw.values === 'object' && !Array.isArray(raw.values) ? raw.values : {};
      const values: Record<string, unknown> = {};
      for (const [pid, prop] of byId) values[pid] = cleanValue(prop, incoming[pid]);
      const created = finite(raw.created, Date.now());
      const comments = Array.isArray(raw.comments) ? raw.comments.slice(0, 500).flatMap((comment: any, commentAt: number) => {
        if (!comment || typeof comment !== 'object') return [];
        const body = typeof comment.body === 'string' ? comment.body.trim().slice(0, 10000) : '';
        if (!body) return [];
        return [{ id: validId(comment.id) ? comment.id : derivedId('c', rid, commentAt), author: text(comment.author, 100) || 'You',
          body, created: finite(comment.created, created) }];
      }) : [];
      out.push({ id: rid, values, body: typeof raw.body === 'string' ? raw.body.slice(0, 500000) : '',
        icon: text(raw.icon, 80) || undefined, cover: typeof raw.cover === 'string' ? raw.cover.slice(0, 4000) : undefined,
        favorite: !!raw.favorite, comments, created, updated: finite(raw.updated, created),
        createdBy: text(raw.createdBy, 100) || 'You', updatedBy: text(raw.updatedBy, 100) || text(raw.createdBy, 100) || 'You' });
    }
    return out;
  }

  /* One view, structurally sanitised. With a property set every reference is
     filtered against the schema (the composed-read rule); with propIds = null
     the shape is validated but property references are kept as opaque strings
     (the independent-document rule — a view document is sanitised without its
     parent schema, and the composed read re-filters, exactly like row values
     keyed by property id). */
  function sanitiseView(v: any, propIds: Set<string> | null, fallbackId?: string): MdDatabaseView | null {
    if (!v || typeof v !== 'object') return null;
    const vid = validId(v.id) ? v.id : fallbackId || id('v');
    const type = VIEW_TYPES.includes(v.type) ? v.type as MdDatabaseViewType : 'table';
    const propOk = (candidate: unknown): candidate is string =>
      typeof candidate === 'string' && (propIds ? propIds.has(candidate) : candidate.length <= 80);
    const propRef = (candidate: unknown): string | null => propOk(candidate) ? candidate : null;
    const propList = (candidate: unknown): string[] => Array.isArray(candidate) ? candidate.filter(propOk) : [];
    const visible = propList(v.visiblePropertyIds);
    const sorts: MdDatabaseSort[] = Array.isArray(v.sorts) ? v.sorts.filter((s: any) => s && propOk(s.propertyId) && ['asc', 'desc'].includes(s.direction)).slice(0, 16).map((s: any) => ({ propertyId: s.propertyId, direction: s.direction })) : [];
    const filters: MdDatabaseFilter[] = Array.isArray(v.filters) ? v.filters.filter((f: any) => f && propOk(f.propertyId) && FILTERS.includes(f.operator)).slice(0, 24).map((f: any) => ({ propertyId: f.propertyId, operator: f.operator, value: f.value })) : [];
    const calculations: Record<string, MdDatabaseCalculation> = {};
    if (v.calculations && typeof v.calculations === 'object') for (const [pid, calculation] of Object.entries(v.calculations)) {
      if (propOk(pid) && CALCULATIONS.includes(calculation as MdDatabaseCalculation) && calculation !== 'none') calculations[pid] = calculation as MdDatabaseCalculation;
    }
    const propertyWidths: Record<string, number> = {};
    if (v.propertyWidths && typeof v.propertyWidths === 'object') for (const [pid, width] of Object.entries(v.propertyWidths)) {
      if (propOk(pid)) propertyWidths[pid] = Math.max(90, Math.min(640, finite(width, 180)));
    }
    const conditionalColors = Array.isArray(v.conditionalColors) ? v.conditionalColors.slice(0, 24).flatMap((rule: any, ruleAt: number) => {
      if (!rule || !propOk(rule.propertyId) || !FILTERS.includes(rule.operator) || !COLORS.includes(rule.color)) return [];
      return [{ id: validId(rule.id) ? rule.id : derivedId('cc', vid, ruleAt), propertyId: rule.propertyId,
        operator: rule.operator as MdDatabaseFilter['operator'], value: rule.value, color: rule.color }];
    }) : [];
    const openPagesIn = ['side_peek', 'center_peek', 'full_page'].includes(v.openPagesIn) ? v.openPagesIn : 'side_peek';
    const chartAggregation = CHART_AGGREGATIONS.includes(v.chartAggregation) ? v.chartAggregation as MdDatabaseChartAggregation
      : propRef(v.chartPropertyId) ? 'sum' : 'count';
    const dashboardAggregations: Record<string, 'sum' | 'average' | 'median' | 'count'> = {};
    if (v.dashboardAggregations && typeof v.dashboardAggregations === 'object') {
      for (const [key, value] of Object.entries(v.dashboardAggregations)) {
        if (propOk(key) && DASHBOARD_AGGREGATIONS.includes(value as string)) dashboardAggregations[key] = value as 'sum' | 'average' | 'median' | 'count';
      }
    }
    return { id: vid, name: text(v.name, 60) || type[0]!.toUpperCase() + type.slice(1), type,
      visiblePropertyIds: visible.length ? visible : (propIds ? [...propIds] : []), sorts, filters,
      groupBy: propRef(v.groupBy), groupDirection: v.groupDirection === 'desc' ? 'desc' : 'asc',
      hiddenGroups: Array.isArray(v.hiddenGroups) ? v.hiddenGroups.filter((x: unknown) => typeof x === 'string').slice(0, 100) : [],
      calculations, datePropertyId: propRef(v.datePropertyId), endDatePropertyId: propRef(v.endDatePropertyId),
      /* How wide a day is drawn, remembered per view — the vocabulary belongs to
         md-timeline.ts, which is loaded before this file precisely so it can be
         asked rather than copied. */
      timelineZoom: (g.MD_TIMELINE_ZOOMS || []).includes(v.timelineZoom) ? v.timelineZoom : 'month',
      chartPropertyId: propRef(v.chartPropertyId), chartType: v.chartType === 'donut' ? 'donut' : 'bar', chartAggregation,
      propertyWidths, frozenPropertyIds: propList(v.frozenPropertyIds), wrapPropertyIds: propList(v.wrapPropertyIds),
      showDataSourceTitle: v.showDataSourceTitle !== false, showVerticalLines: v.showVerticalLines !== false,
      showPageIcon: v.showPageIcon !== false, wrapContent: v.wrapContent !== false,
      openPagesIn: openPagesIn as MdDatabaseView['openPagesIn'], conditionalColors, dashboardAggregations };
  }

  function views(value: unknown, props: MdDatabaseProperty[], anchor: string): MdDatabaseView[] {
    const raw = Array.isArray(value) ? value : [], out: MdDatabaseView[] = [], seen = new Set<string>();
    const propIds = new Set(props.map((p) => p.id));
    for (const [at, item] of raw.slice(0, MAX_VIEWS).entries()) {
      if (!item || typeof item !== 'object') continue;
      const view = sanitiseView(item, propIds, derivedId('v', anchor, at));
      if (!view || seen.has(view.id)) continue; seen.add(view.id);
      out.push(view);
    }
    if (!out.length) out.push({ id: derivedId('v', anchor, 0), name: 'Table', type: 'table',
      visiblePropertyIds: props.map((p) => p.id), sorts: [], filters: [], groupBy: null,
      groupDirection: 'asc', hiddenGroups: [], calculations: {}, datePropertyId: null,
      endDatePropertyId: null, chartPropertyId: null, chartType: 'bar', chartAggregation: 'count', propertyWidths: {},
      frozenPropertyIds: [], wrapPropertyIds: [], showDataSourceTitle: true, showVerticalLines: true,
      showPageIcon: true, wrapContent: true, openPagesIn: 'side_peek', conditionalColors: [], dashboardAggregations: {} });
    return out;
  }

  function mdDatabaseSanitise(value: unknown, expectedId?: unknown): MdDatabase | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as any;
    const dbId = validId(raw.id) ? raw.id : validId(expectedId) ? expectedId : null;
    if (!dbId || (validId(expectedId) && dbId !== expectedId)) return null;
    const props = properties(raw.properties, dbId);
    /* Views arrive in one of two shapes. Legacy (pre-1C) documents embed the
       view objects — sanitise them here; the caller migrates them to their
       own documents. Contracted documents carry an ordered list of view ids —
       the views themselves live in md:view:<id> documents and are hydrated by
       the read/apply paths, so this layer keeps the list as-is and mints no
       default (an id list it cannot resolve is not an empty view set). */
    const rawViews = Array.isArray(raw.views) ? raw.views : null;
    const hasEmbedded = !!rawViews && rawViews.some((item: unknown) => !!item && typeof item === 'object');
    const cleanViews = hasEmbedded || !rawViews || rawViews.length === 0 ? views(rawViews, props, dbId) : [];
    const active = cleanViews.some((v) => v.id === raw.activeViewId) ? String(raw.activeViewId)
      : cleanViews.length ? cleanViews[0]!.id
      : validId(raw.activeViewId) ? String(raw.activeViewId) : '';
    const created = finite(raw.created, Date.now());
    const templates: MdDatabaseTemplate[] = Array.isArray(raw.templates) ? raw.templates.slice(0, 50).flatMap((template: any, templateAt: number) => {
      if (!template || typeof template !== 'object') return [];
      return [{ id: validId(template.id) ? template.id : derivedId('t', dbId, templateAt), name: text(template.name, 120) || 'New template',
        icon: text(template.icon, 80) || undefined, body: typeof template.body === 'string' ? template.body.slice(0, 500000) : '',
        values: template.values && typeof template.values === 'object' && !Array.isArray(template.values) ? clone(template.values) : {} }];
    }) : [];
    const automations: MdDatabaseAutomation[] = Array.isArray(raw.automations) ? raw.automations.slice(0, 50).flatMap((automation: any, automationAt: number) => {
      if (!automation || typeof automation !== 'object') return [];
      const trigger = automation.trigger === 'property_changed' ? 'property_changed' : 'row_added';
      const action = automation.action === 'add_row' ? 'add_row' : 'set_property';
      const clean: MdDatabaseAutomation = { id: validId(automation.id) ? automation.id : derivedId('a', dbId, automationAt), name: text(automation.name, 120) || 'New automation',
        enabled: automation.enabled !== false, trigger, triggerPropertyId: typeof automation.triggerPropertyId === 'string' ? automation.triggerPropertyId : undefined,
        action, actionPropertyId: typeof automation.actionPropertyId === 'string' ? automation.actionPropertyId : undefined };
      if ('value' in automation) clean.value = clone(automation.value);
      return [clean];
    }) : [];
    /* Where this library LIVES (0.2). A ref key, exactly as a desk's is — the
       one upward pointer, spelled the same way at every altitude. Sanitised
       through mdRecordRefParse rather than kept as typed, because everything
       else here is, and a pointer that survives sanitisation unchecked is how a
       path gets into a key. */
    const scope = typeof raw.scopeRef === 'string' && g.mdRecordRefParse
      && g.mdRecordRefParse(raw.scopeRef) ? String(raw.scopeRef) : null;
    return { version: VERSION, id: dbId, name: text(raw.name, 120) || 'Untitled database',
      properties: props, rows: rows(raw.rows, props, dbId), views: cleanViews, activeViewId: active,
      locked: !!raw.locked, templates, automations, ...(scope ? { scopeRef: scope } : {}),
      created, updated: finite(raw.updated, created), revision: Math.max(1, finite(raw.revision, 1)) };
  }

  const keyFor = (dbId: string): string => `md:database:${dbId}`;
  /* The family, named once. Every caller below asks storage for this prefix
     rather than for the whole key space and filtering — which is the same
     answer, and was the single largest source of the 138,015 keys a 5,110-key
     account materialised to draw one screen. */
  const DB_PREFIX = 'md:database:';
  const rowKeyFor = (dbId: string, rowId: string): string => `md:database-row:${dbId}:${rowId}`;
  const rowPrefixFor = (dbId: string): string => `md:database-row:${dbId}:`;

  /* ── the CRDT beside a row (E5) ───────────────────────────────────────────
     A row is one document and it has always been reconciled whole: the pull
     writes the incoming copy over the local one, and a row this device has
     edited refuses the incoming copy and pushes its own back instead. Either
     way one device's answers win the WHOLE row — so two people filling in two
     different columns, which is the ordinary thing to do with a shared library,
     silently lose one of the answers.

     The state rides in its OWN key, exactly as it does beside a day and for
     exactly the same reason: md-search.ts JSON.parses every stored value, and
     an opaque row would leave the Finder with nothing to repair it.

     THE ROW DOCUMENT'S OWN RULES ARE UNCHANGED — only the state key is declared
     mergeable. A row has no tombstone family, so a null for a row must keep
     being refused by a device holding unpushed work exactly as it is today;
     calling a row "mergeable" would quietly change what a deletion does. */
  const rowCrdtKeyFor = (dbId: string, rowId: string): string => `md:crdt:database-row:${dbId}:${rowId}`;
  const ROW_CRDT_RE = /^crdt:database-row:([a-z0-9]{6,40}):([a-z0-9]{6,40})$/;
  const ROW_CRDT_PREFIX = 'md:crdt:database-row:';
  const crdtOn = (): boolean =>
    typeof g.mdCrdtSeedFields === 'function' && typeof g.mdCrdtApplyFields === 'function';

  /* Bring the state beside this row up to date with what was just stored.

     `seed` is the difference between a row this device WROTE and one it was
     HANDED. A state started here for a row that arrived is a rival history —
     the state describing it is already on its way, and two states seeded
     independently share no past, so the merge would keep one of them whole for
     ever instead of merging cell by cell. Adopting theirs is what gives the two
     devices a common ancestor, and it is only possible while this device holds
     nothing. Follow a state that exists, always; start one only for local work.

     A state that cannot be read is RESEEDED rather than repaired: it is derived
     data, so throwing it away costs nothing. A null row removes it — unlike a
     day, a row's deletion travels on its own key, so a state left behind here
     would be a ghost with nothing to hold it down. */
  function rowCrdtFollow(dbId: string, rowId: string, row: unknown, seed: boolean): void {
    if (!crdtOn()) return;
    const key = rowCrdtKeyFor(dbId, rowId);
    if (row == null) { g.mdRemove(key); return; }
    const held = g.mdRead(key, null);
    if (!held && !seed) return;
    const next = held
      ? (g.mdCrdtApplyFields(held, row) || g.mdCrdtSeedFields(row))
      : g.mdCrdtSeedFields(row);
    if (next) g.mdWrite(key, next);
  }

  /** Whether applying this key MERGES rather than overwrites — the question the
      pull loop asks before it refuses a document that conflicts with unpushed
      local work. Only a state does. */
  function mdDatabaseMergesDoc(key: unknown): boolean {
    return typeof key === 'string' && ROW_CRDT_RE.test(key) && crdtOn();
  }
  /* The stored/on-the-wire database document: no rows (they are their own
     documents) and no view objects (views are their own documents — only the
     ordered id list rides here). viewIds overrides the derived list for the
     partial-hydration case: a rewrite must never drop an id whose document
     has not landed yet. */
  const metadataOf = (db: MdDatabase, viewIds?: string[] | null): MdDatabaseMetaDoc =>
    ({ ...clone(db), rows: [], views: viewIds ?? db.views.map((view) => view.id) });

  /* The database document AS THE WIRE CARRIES IT — the one function the sync
     layer asks, for both directions at once: what it returns is what is
     OFFERED, and also what a pull records as this device's hash. Those two
     must be the same bytes for the same database or the device pushes the
     same document every sync for ever (md-sync.js, "an ENDLESS SYNC").

     mdDatabaseSanitise cannot be that function on its own: it answers in the
     WORKING shape, whose `views` are objects, and a contracted id list goes
     into it as strings and comes out as `[]` — the callers that store
     re-contract from prepareViews, but an offer normalised through it had its
     view list stripped, which is how every database on the account came to
     sit on the server with `views: []` and why an empty list stormed at all
     (staging, 2026-09-01). So a CONTRACTED document is given back contracted,
     the id list validated rather than expanded. A document with embedded or
     absent views still answers in the working shape: the applier migrates
     embedded views to their own documents and must see them whole. */
  function mdDatabaseSanitiseSyncDoc(value: unknown, dbId?: unknown): MdDatabase | MdDatabaseMetaDoc | null {
    const clean = mdDatabaseSanitise(value, dbId);
    if (!clean) return null;
    const rawViews = Array.isArray((value as any).views) ? (value as any).views : null;
    const hasEmbedded = !!rawViews && rawViews.some((item: unknown) => !!item && typeof item === 'object');
    if (!rawViews || !rawViews.length || hasEmbedded) return clean;
    return metadataOf(clean, rawViews.filter(validId));
  }

  function mdDatabaseRowSanitise(value: unknown, expectedRowId?: unknown): MdDatabaseRow | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as any, rowId = validId(raw.id) ? raw.id : validId(expectedRowId) ? expectedRowId : null;
    if (!rowId || (validId(expectedRowId) && rowId !== expectedRowId) || !raw.values || typeof raw.values !== 'object' || Array.isArray(raw.values)) return null;
    const created = finite(raw.created, Date.now());
    const comments = Array.isArray(raw.comments) ? raw.comments.slice(0, 500).flatMap((comment: any, commentAt: number) => {
      const body = comment && typeof comment.body === 'string' ? comment.body.trim().slice(0, 10000) : '';
      return body ? [{ id: validId(comment.id) ? comment.id : derivedId('c', rowId, commentAt), author: text(comment.author, 100) || 'You',
        body, created: finite(comment.created, created) }] : [];
    }) : [];
    return { id: rowId, values: clone(raw.values), body: typeof raw.body === 'string' ? raw.body.slice(0, 500000) : '',
      icon: text(raw.icon, 80) || undefined, cover: typeof raw.cover === 'string' ? raw.cover.slice(0, 4000) : undefined,
      favorite: !!raw.favorite, comments, created, updated: finite(raw.updated, created), createdBy: text(raw.createdBy, 100) || 'You',
      updatedBy: text(raw.updatedBy, 100) || text(raw.createdBy, 100) || 'You' };
  }
  const viewKeyFor = (viewId: string): string => `md:view:${viewId}`;
  const VIEW_KEY_RE = /^view:([a-z0-9]{6,40})$/;

  /* Structural sanitise of an independent view document. Property references
     are validated as strings only — the parent schema is deliberately not
     consulted here (the document must be acceptable before its parent
     arrives, mirroring row documents); the composed read re-filters against
     the live schema. */
  function mdViewDocSanitise(value: unknown, expectedViewId?: unknown): MdViewDoc | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as any;
    const vid = validId(raw.id) ? raw.id : validId(expectedViewId) ? expectedViewId : null;
    if (!vid || (validId(expectedViewId) && vid !== expectedViewId)) return null;
    if (!validId(raw.databaseId)) return null;
    const view = sanitiseView({ ...raw, id: vid }, null);
    if (!view) return null;
    return { version: 1, ...view, databaseId: String(raw.databaseId),
      updated: finite(raw.updated, Date.now()), revision: Math.max(1, finite(raw.revision, 1)) };
  }
  function docFromView(view: MdDatabaseView, databaseId: string, revision: number, updated: number): MdViewDoc {
    return { version: 1, ...clone(view), databaseId, updated, revision };
  }
  function viewFromDoc(doc: MdViewDoc): MdDatabaseView {
    const { version, databaseId, updated, revision, ...view } = doc;
    void version; void databaseId; void updated; void revision;
    return view;
  }
  function viewDocFromStorage(viewId: string): MdViewDoc | null {
    return mdViewDocSanitise(g.mdRead(viewKeyFor(viewId), null), viewId);
  }

  /* The expand → migrate → contract boundary for views, shared by the read,
     save and sync-apply paths. Given a raw database document:
     - legacy embedded view objects are minted as md:view:<id> documents (a
       view document that already exists is the newer, independently revisioned
       authority and is never clobbered), the stored metadata is rewritten in
       the contracted id-list form, and the working copy is hydrated from the
       view documents;
     - a contracted id list is hydrated from the view documents (ids whose
       documents have not arrived yet stay in the list — they hydrate when
       they land);
     - the returned contractedIds tell the caller which id list any metadata
       rewrite must carry, so a partial hydration can never drop a view. */
  function prepareViews(raw: any, dbId: string): { working: any; contractedIds: string[] | null } | null {
    const rawViews = Array.isArray(raw.views) ? raw.views : [];
    const embedded = rawViews.some((item: unknown) => !!item && typeof item === 'object');
    const idList = rawViews.filter((item: unknown): item is string => typeof item === 'string' && validId(item));
    const hydrate = (ids: string[]): MdDatabaseView[] => ids.flatMap((vid) => {
      const doc = viewDocFromStorage(vid);
      return doc && doc.databaseId === dbId ? [viewFromDoc(doc)] : [];
    });
    if (embedded) {
      const legacyClean = mdDatabaseSanitise(raw, dbId);
      if (!legacyClean) return null;
      for (const view of legacyClean.views) {
        if (!viewDocFromStorage(view.id)) g.mdWrite(viewKeyFor(view.id), docFromView(view, dbId, 1, legacyClean.updated));
      }
      const contractedIds = legacyClean.views.map((view) => view.id);
      g.mdWrite(keyFor(dbId), metadataOf(legacyClean));
      return { working: { ...raw, views: hydrate(contractedIds), activeViewId: legacyClean.activeViewId }, contractedIds };
    }
    if (idList.length) return { working: { ...raw, views: hydrate(idList) }, contractedIds: idList };
    return { working: raw, contractedIds: null };
  }

  function notify(dbId: string, value: MdDatabase | null): void {
    const snapshot = value ? clone(value) : null;
    if (typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') g.dispatchEvent(new g.CustomEvent('mddatabasechange', { detail: { id: dbId, value: snapshot } }));
    if (typeof g.mdSyncSoon === 'function') g.mdSyncSoon();
  }
  function historyFor(dbId: string): { undo: MdDatabaseHistoryItem[]; redo: MdDatabaseHistoryItem[] } {
    let history = historyByDatabase.get(dbId);
    if (!history) { history = { undo: [], redo: [] }; historyByDatabase.set(dbId, history); }
    return history;
  }
  function notifyHistory(dbId: string): void {
    if (typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') {
      g.dispatchEvent(new g.CustomEvent('mddatabasehistorychange', { detail: { id: dbId, ...mdDatabaseHistoryState(dbId) } }));
    }
  }
  function historyComparable(db: MdDatabase): string {
    return JSON.stringify({ ...db, updated: 0, revision: 0, rows: db.rows.map((row) => ({ ...row })) });
  }
  function pushHistory(dbId: string, item: MdDatabaseHistoryItem): void {
    const history = historyFor(dbId);
    history.undo.push(item);
    if (history.undo.length > HISTORY_LIMIT) history.undo.splice(0, history.undo.length - HISTORY_LIMIT);
    history.redo = [];
    notifyHistory(dbId);
  }
  function recordHistory(before: MdDatabase, after: MdDatabase): void {
    if (historyComparable(before) === historyComparable(after)) return;
    pushHistory(before.id, { kind: 'db', value: clone(before) });
  }
  /* The membership snapshot taken before a member mutation. mdMembershipList
     builds fresh objects on every call, so the snapshot cannot be mutated
     from under the stack. */
  function recordMembershipHistory(dbId: string, before: unknown[]): void {
    pushHistory(dbId, { kind: 'membership', value: before });
  }
  function membershipNow(dbId: string): unknown[] {
    return typeof g.mdMembershipList === 'function' ? g.mdMembershipList(dbId) : [];
  }
  function mdDatabaseHistoryState(dbId: unknown): { canUndo: boolean; canRedo: boolean } {
    if (!validId(dbId)) return { canUndo: false, canRedo: false };
    const history = historyFor(dbId);
    return { canUndo: history.undo.length > 0, canRedo: history.redo.length > 0 };
  }
  /* Undo and redo walk one stack that holds both document kinds; each step
     restores exactly the document the mutation touched and parks the current
     state on the opposite stack. */
  function historyStep(dbId: string, from: MdDatabaseHistoryItem[], to: MdDatabaseHistoryItem[]): MdDatabase | null {
    const target = from.pop(), current = mdDatabaseRead(dbId);
    if (!target || !current) return current;
    if (target.kind === 'membership') {
      const parked = membershipNow(dbId);
      if (typeof g.mdMembershipReplace !== 'function' || !g.mdMembershipReplace(dbId, target.value)) {
        from.push(target);
        notifyHistory(dbId);
        return current;
      }
      to.push({ kind: 'membership', value: parked });
      const fresh = mdDatabaseRead(dbId);
      notify(dbId, fresh);
      notifyHistory(dbId);
      return fresh || current;
    }
    /* The record's own home, put back through the door it was written through.
       The librarian may refuse — a project deleted since the write, a record
       gone — and a refused step must leave the stack exactly as it found it,
       because a lost step is a promise broken silently. */
    if (target.kind === 'record') {
      const parked = typeof g.mdLibrarianCellRead === 'function'
        ? g.mdLibrarianCellRead(target.key, target.propertyId) : null;
      const wrote = (parked !== null && typeof g.mdLibrarianCellSet === 'function')
        ? g.mdLibrarianCellSet(target.key, target.propertyId, target.value)
        : { ok: false };
      if (!wrote.ok) {
        from.push(target);
        notifyHistory(dbId);
        return current;
      }
      to.push({ kind: 'record', key: target.key, propertyId: target.propertyId, value: parked });
      const fresh = mdDatabaseRead(dbId);
      notify(dbId, fresh);
      notifyHistory(dbId);
      return fresh || current;
    }
    if (target.kind === 'both') {
      // one sweep took a native row and a member together; one step returns both
      const parkedMembership = membershipNow(dbId), parkedDb = clone(current);
      if (typeof g.mdMembershipReplace !== 'function' || !g.mdMembershipReplace(dbId, target.membership)) {
        from.push(target);
        notifyHistory(dbId);
        return current;
      }
      const saved = mdDatabaseSave(target.db);
      if (!saved) {
        g.mdMembershipReplace(dbId, parkedMembership);
        from.push(target);
        notifyHistory(dbId);
        return current;
      }
      to.push({ kind: 'both', db: parkedDb, membership: parkedMembership });
      notifyHistory(dbId);
      return saved;
    }
    to.push({ kind: 'db', value: clone(current) });
    const saved = mdDatabaseSave(target.value);
    if (!saved) { from.push(target); to.pop(); }
    notifyHistory(dbId);
    return saved || current;
  }
  function mdDatabaseUndo(dbId: unknown): MdDatabase | null {
    if (!validId(dbId)) return null;
    const history = historyFor(dbId);
    return historyStep(dbId, history.undo, history.redo);
  }
  function mdDatabaseRedo(dbId: unknown): MdDatabase | null {
    if (!validId(dbId)) return null;
    const history = historyFor(dbId);
    return historyStep(dbId, history.redo, history.undo);
  }
  function canonical(value: MdDatabase): MdDatabase {
    canonicalDatabases.add(value);
    return value;
  }
  function mdDatabaseCacheReset(dbId?: unknown): void {
    if (validId(dbId)) canonicalByDatabase.delete(dbId);
    else canonicalByDatabase.clear();
  }
  /** Everything this module remembers, dropped — including the undo stacks,
      which mdDatabaseCacheReset deliberately keeps. That distinction is the
      whole point of having two functions: a sync pull changes a document under
      us, but the workspace is still the user's own and their undo history is
      still theirs to walk. An IMPORT is not that. It replaces the workspace
      wholesale, which makes the parsed databases stale and makes the undo stack
      something worse than stale — it holds documents from the file the user
      just replaced, so one undo after a restore would write the old workspace
      over the new one. Called by md-backup.js, which writes through mdWriteRaw
      and therefore under every module that would otherwise have noticed. */
  function mdDatabaseForgetAll(): void {
    canonicalByDatabase.clear();
    historyByDatabase.clear();
  }
  function mdDatabaseReadCanonical(dbId: unknown): MdDatabase | null {
    if (!validId(dbId)) return null;
    const cached = canonicalByDatabase.get(dbId);
    if (cached) return cached;
    const rawStored = g.mdRead(keyFor(dbId), null);
    /* The Main DB is materialised, not stored — the built-in pattern md-project
       shows for the Prayers project, one shelf up. It answers before anybody has
       made it, and the moment the user changes something (a column, a view) a
       real document is saved and read from here on. Its rows are never its own:
       they are every record on the account, composed at view time.

       The shipped definition deliberately does NOT go through prepareViews. That
       function reads embedded view objects as a legacy document to migrate, and
       migrating WRITES — one document per view, plus the metadata document for
       the database itself. Running it here would save the Main DB to storage on
       the first read that ever touched it, which is precisely what "materialised,
       not stored" means not to do. There is nothing to migrate either: a database
       nobody has made yet has no stored views to contract. Once the user does
       change something, a real document exists and every read after that takes
       the stored path below, views and all. */
    if (rawStored == null) {
      const shipped = mdDatabaseSanitise(shippedDatabase(dbId), dbId);
      if (!shipped) return null;
      const bare = mdDatabaseSanitise({ ...shipped, rows: [] }, dbId);
      if (!bare) return null;
      canonicalByDatabase.set(dbId, canonical(bare));
      return bare;
    }
    if (typeof rawStored !== 'object' || Array.isArray(rawStored)) return null;
    const prepared = prepareViews(rawStored, String(dbId));
    if (!prepared) return null;
    const metadata = mdDatabaseSanitise(prepared.working, dbId);
    if (!metadata) return null;
    if (isShipped(dbId)) {
      const bare = mdDatabaseSanitise({ ...metadata, rows: [] }, dbId);
      if (!bare) return null;
      canonicalByDatabase.set(dbId, canonical(bare));
      return bare;
    }
    const rawRows = new Map<string, MdDatabaseRow>(metadata.rows.map((row) => [row.id, row]));
    /* This database's rows, asked for by name. It used to be every key on the
       device filtered down to nine hundred — which is the same answer, arrived
       at by reading four thousand two hundred keys that could not possibly
       match. */
    const prefix = rowPrefixFor(dbId);
    for (const key of g.mdStorageKeys(prefix)) {
      const rowId = key.slice(prefix.length), row = mdDatabaseRowSanitise(g.mdRead(key, null), rowId);
      if (row) rawRows.set(row.id, row);
    }
    if (metadata.rows.length) {
      for (const row of metadata.rows) {
        g.mdWrite(rowKeyFor(dbId, row.id), row);
        rowCrdtFollow(dbId, row.id, row, true);   // this device's own rows, moving house
      }
      g.mdWrite(keyFor(dbId), metadataOf(metadata, prepared.contractedIds));
    }
    const clean = mdDatabaseSanitise({ ...metadata, rows: [...rawRows.values()] }, dbId);
    if (!clean) return null;
    canonicalByDatabase.set(dbId, canonical(clean));
    return clean;
  }
  function mdDatabaseRead(dbId: unknown): MdDatabase | null {
    const clean = mdDatabaseReadCanonical(dbId);
    return clean ? clone(clean) : null;
  }
  /* Which database holds this row? A dbrow ref names the row alone — the ref
     key's middle slot belongs to a calendar day, and an id cannot hold a colon,
     so the database deliberately does not ride along (see md-record.ts). This is
     a scan of the databases this device holds — canonical reads, so after the
     first pass it touches no storage. md:record-index (md-record-index.ts) now
     knows every row's home and could answer without the scan; it stays a scan
     because this is the one caller that must be right when the index is stale,
     and the index is derived, never authoritative. */
  /* Every row on the device, keyed by id, built at most once between writes.
     This used to walk mdStorageKeys() on EVERY call, and the Librarian asks it
     once per database row in the catalogue: a real 5,000-record account spent
     8.2 of its 9 seconds here, which is what a frozen app looks like from the
     inside.

     Built in storage-key order and first-match-wins, so the answer is byte for
     byte what the walk used to give — including for a row id authored into two
     databases, which still resolves to whichever database storage lists first.
     That ambiguity is a separate bug and is deliberately NOT changed here. */
  function rowIndexNow(): Map<string, { databaseId: string; row: MdDatabaseRow }> {
    if (rowIndex) return rowIndex;
    const built = new Map<string, { databaseId: string; row: MdDatabaseRow }>();
    for (const key of g.mdStorageKeys(DB_PREFIX)) {
      const db = mdDatabaseReadCanonical(key.slice(DB_PREFIX.length));
      if (!db) continue;
      for (const row of db.rows) if (!built.has(row.id)) built.set(row.id, { databaseId: db.id, row });
    }
    /* Assigned only after the loop, and that is load-bearing: every
       mdDatabaseReadCanonical above may populate the canonical cache, and the
       wrapper nulls this field when it does. */
    rowIndex = built;
    return built;
  }
  function mdDatabaseRowFind(rowId: unknown): { databaseId: string; row: MdDatabaseRow } | null {
    if (!validId(rowId)) return null;
    const hit = rowIndexNow().get(String(rowId));
    return hit ? { databaseId: hit.databaseId, row: clone(hit.row) } : null;
  }
  function mdDatabaseList(): MdDatabase[] {
    const found: MdDatabase[] = [];
    for (const key of g.mdStorageKeys(DB_PREFIX)) {
      const db = mdDatabaseReadCanonical(key.slice(DB_PREFIX.length)); if (db) found.push(clone(db));
    }
    found.sort((a, b) => a.name.localeCompare(b.name));
    /* The Main DB above every database the user made, whether or not a document
       for it exists yet. It is the account's own catalogue; a list that files it
       under M is a list that has forgotten what it is. */
    /* Unshifted in reverse, so they come out in the order the app names them:
       the account's own catalogue, then the people in it. */
    for (const shippedId of [g.MD_OMANA_ID, g.MD_LIBRARIAN_ID]) {
      if (!shippedId) continue;
      const at = found.findIndex((db) => db.id === shippedId);
      const shipped = at >= 0 ? found.splice(at, 1)[0] : mdDatabaseRead(shippedId);
      if (shipped) found.unshift(shipped);
    }
    return found;
  }
  /** Is this a database ManDana ships rather than one somebody made? The engine
      already refuses a row add and a delete on the Main DB; this is the same
      question asked from outside, so a surface can decline to DRAW a command
      the door would refuse. A menu that offers what the engine refuses teaches
      the user the app is broken. */
  function mdDatabaseBuiltin(dbId: unknown): boolean {
    return isShipped(dbId);
  }
  /* The slash picker's existing-half: databases whose name matches the query,
     light enough to list under a caret. The "new database" row is the host's
     own business — it is the one that holds the creation callback. */
  function mdDatabasePickerRows(query: unknown): Array<{ id: string; name: string }> {
    const needle = typeof query === 'string' ? query.trim().toLocaleLowerCase() : '';
    return mdDatabaseList()
      .filter((db) => !needle || db.name.toLocaleLowerCase().includes(needle))
      .slice(0, 8)
      .map((db) => ({ id: db.id, name: db.name }));
  }
  function mdDatabaseSave(value: unknown): MdDatabase | null {
    /* Accept the contracted shape too: hydrate any id list before sanitising,
       so a caller holding a raw document can never strip the views. */
    let workingValue = value;
    if (workingValue && typeof workingValue === 'object' && !Array.isArray(workingValue)) {
      const rawInput = workingValue as any;
      if (validId(rawInput.id) && Array.isArray(rawInput.views) && rawInput.views.some((item: unknown) => typeof item === 'string')) {
        const prepared = prepareViews(rawInput, String(rawInput.id));
        if (!prepared) return null;
        workingValue = prepared.working;
      }
    }
    const incoming = mdDatabaseSanitise(workingValue); if (!incoming) return null;
    const prior = mdDatabaseReadCanonical(incoming.id), now = Date.now();
    const priorMeta = prior ? metadataOf(prior) : null;
    const candidateMeta = metadataOf({ ...incoming, created: prior ? prior.created : incoming.created });
    const comparableMeta = (doc: MdDatabaseMetaDoc | null): string => doc ? JSON.stringify({ ...doc, updated: 0, revision: 0 }) : '';
    const priorViews = new Map((prior ? prior.views : []).map((view) => [view.id, view])), nextViewIds = new Set(incoming.views.map((view) => view.id));
    /* Hydration-gap guard: the stored metadata may name view ids the composed
       read could not hydrate (their documents have not landed on this device
       yet). Those ids are in neither the prior nor the next composed list, so
       the diff below never touches their documents — keep them in the id list
       or this save would orphan views that are still in flight. An id whose
       document is genuinely gone (tombstoned) drops out here and the list
       self-heals. */
    const storedRaw = g.mdRead(keyFor(incoming.id), null);
    const storedIds: string[] = storedRaw && typeof storedRaw === 'object' && Array.isArray((storedRaw as any).views)
      ? (storedRaw as any).views.filter((item: unknown): item is string => typeof item === 'string' && validId(item)) : [];
    const composedIds = new Set([...priorViews.keys(), ...nextViewIds]);
    const gapIds = storedIds.filter((vid) => !composedIds.has(vid) && !!viewDocFromStorage(vid));
    const metaChanged = !priorMeta || comparableMeta(priorMeta) !== comparableMeta(candidateMeta) || gapIds.length > 0;
    const next = { ...incoming, created: prior ? prior.created : incoming.created,
      updated: metaChanged ? now : prior!.updated,
      revision: metaChanged ? (prior ? prior.revision + 1 : Math.max(1, incoming.revision)) : prior!.revision };
    const priorRows = new Map((prior ? prior.rows : []).map((row) => [row.id, row])), nextIds = new Set(next.rows.map((row) => row.id));
    const operations: Array<{ key: string; value?: unknown; remove?: true }> = [];
    if (metaChanged) operations.push({ key: keyFor(next.id), value: metadataOf(next, gapIds.length ? [...next.views.map((view) => view.id), ...gapIds] : null) });
    for (const row of next.rows) {
      const before = priorRows.get(row.id);
      if (!before || JSON.stringify(before) !== JSON.stringify(row)) operations.push({ key: rowKeyFor(next.id, row.id), value: row });
    }
    for (const rowId of priorRows.keys()) if (!nextIds.has(rowId)) operations.push({ key: rowKeyFor(next.id, rowId), remove: true });
    /* Views diff exactly like rows: each changed view writes only its own
       document with its own revision; a view-content change never touches the
       database document (that is the CAS isolation this phase exists for). */
    for (const view of next.views) {
      const before = priorViews.get(view.id);
      if (!before || JSON.stringify(before) !== JSON.stringify(view)) {
        const stored = viewDocFromStorage(view.id);
        operations.push({ key: viewKeyFor(view.id), value: docFromView(view, next.id, stored ? stored.revision + 1 : 1, now) });
      }
    }
    for (const viewId of priorViews.keys()) if (!nextViewIds.has(viewId)) operations.push({ key: viewKeyFor(viewId), remove: true });

    // A database mutation may span metadata and many row documents. Keep the
    // local commit atomic so quota failures cannot leave a half-pasted range.
    const applied: Array<{ key: string; before: string | null }> = [];
    for (const operation of operations) {
      const before = g.mdReadRaw(operation.key);
      const ok = operation.remove ? g.mdRemove(operation.key) : g.mdWrite(operation.key, operation.value);
      if (ok) { applied.push({ key: operation.key, before }); continue; }
      for (let index = applied.length - 1; index >= 0; index--) {
        const rollback = applied[index]!;
        if (rollback.before == null) g.mdRemove(rollback.key);
        else g.mdWriteRaw(rollback.key, rollback.before);
      }
      return null;
    }
    /* The state follows each row that actually changed — after the whole commit
       has landed, never inside the loop, or a row rolled back by a later quota
       failure would leave a state describing a row that was never stored.
       `operations` is the diff already computed above, so an idle save touches
       no state at all. */
    if (crdtOn()) {
      const rowPrefix = rowPrefixFor(next.id);
      for (const operation of operations) {
        if (!operation.key.startsWith(rowPrefix)) continue;
        rowCrdtFollow(next.id, operation.key.slice(rowPrefix.length),
          operation.remove ? null : operation.value, true);
      }
    }
    const clean = mdDatabaseSanitise(next, next.id);
    if (!clean) return null;
    canonicalByDatabase.set(next.id, canonical(clean));
    notify(next.id, clean); return clone(clean);
  }

  function mdDatabaseCreate(name: unknown = 'Untitled database'): MdDatabase | null {
    const dbId = id('d'), now = Date.now();
    const title: MdDatabaseProperty = { id: 'title', name: 'Name', type: 'title' };
    const view: MdDatabaseView = { id: id('v'), name: 'Table', type: 'table', visiblePropertyIds: ['title'],
      sorts: [], filters: [], groupBy: null, groupDirection: 'asc', hiddenGroups: [], calculations: {},
      datePropertyId: null, endDatePropertyId: null, chartPropertyId: null, chartType: 'bar', chartAggregation: 'count',
      propertyWidths: {}, frozenPropertyIds: [], wrapPropertyIds: [], showDataSourceTitle: true,
      showVerticalLines: true, showPageIcon: true, wrapContent: true, openPagesIn: 'side_peek',
      conditionalColors: [], dashboardAggregations: {} };
    return mdDatabaseSave({ version: VERSION, id: dbId, name: text(name, 120) || 'Untitled database', properties: [title],
      rows: [], views: [view], activeViewId: view.id, locked: false, templates: [], automations: [],
      created: now, updated: now, revision: 1 });
  }
  function change(dbId: unknown, edit: (db: MdDatabase) => void): MdDatabase | null {
    const current = mdDatabaseRead(dbId); if (!current) return null;
    const next = clone(current); edit(next); const saved = mdDatabaseSave(next);
    if (saved) recordHistory(current, saved);
    return saved;
  }
  function defaultName(type: MdDatabasePropertyType): string {
    const names: Record<MdDatabasePropertyType, string> = { title: 'Name', text: 'Text', number: 'Number', select: 'Select',
      multi: 'Multi-select', status: 'Status', date: 'Date', checkbox: 'Checkbox', url: 'URL', email: 'Email', phone: 'Phone',
      person: 'Person', files: 'Files & media', formula: 'Formula', relation: 'Relation', rollup: 'Rollup', button: 'Button',
      id: 'ID', place: 'Place', created_time: 'Created time', created_by: 'Created by', updated_time: 'Last edited time', updated_by: 'Last edited by' };
    return names[type];
  }
  function makeProperty(type: MdDatabasePropertyType, name?: unknown): MdDatabaseProperty {
    const out: MdDatabaseProperty = { id: id('p'), name: text(name, 80) || defaultName(type), type };
    if (type === 'status') out.options = [{ id: id('o'), name: 'Not started', color: 'blue' },
      { id: id('o'), name: 'In progress', color: 'amber' }, { id: id('o'), name: 'Done', color: 'mint' }];
    else if (type === 'select' || type === 'multi') out.options = [];
    else if (type === 'number') out.format = 'number';
    else if (type === 'formula') out.formula = '';
    else if (type === 'button') out.button = { label: 'Open', action: 'open_row' };
    return out;
  }
  /** Move this library under something, or to the root (0.2).

      A door onto `change`, exactly as the rename is. The RULE about what may
      hold what lives in md-contain.ts and is applied by md-parent.ts; this
      writes the pointer, so the sync applier restoring a decision does not have
      to re-take it. */
  function mdDatabaseSetScope(dbId: unknown, scopeRef: unknown): boolean {
    const db = mdDatabaseRead(dbId);
    if (!db) return false;
    if (scopeRef == null || scopeRef === '') {
      return !!change(dbId, (next) => { delete next.scopeRef; });
    }
    const parsed = typeof scopeRef === 'string' && g.mdRecordRefParse
      ? g.mdRecordRefParse(scopeRef) : null;
    if (!parsed) return false;
    if (g.mdContainAllows && !g.mdContainAllows(parsed.type, 'database')) return false;
    const key = g.mdRecordRefKey(parsed);
    return !!change(dbId, (next) => { next.scopeRef = key; });
  }

  function mdDatabaseRename(dbId: unknown, name: unknown): MdDatabase | null {
    const clean = text(name, 120); return clean ? change(dbId, (db) => { db.name = clean; }) : mdDatabaseRead(dbId);
  }
  function mdDatabaseTryRename(dbId: unknown, name: unknown): MdDatabaseMutationResult {
    const db = mdDatabaseRead(dbId);
    if (!db) return { ok: false, reason: 'missing' };
    if (db.locked) return { ok: false, reason: 'locked' };
    const clean = text(name, 120);
    if (!clean) return { ok: false, reason: 'invalid' };
    if (clean === db.name) return { ok: true, value: db, changed: false };
    const saved = mdDatabaseRename(db.id, clean);
    return saved ? { ok: true, value: saved, changed: true } : mutationFailure();
  }
  function mdDatabasePropertyAdd(dbId: unknown, typeValue: unknown, name?: unknown): MdDatabase | null {
    const type = TYPES.includes(typeValue as MdDatabasePropertyType) && typeValue !== 'title' ? typeValue as MdDatabasePropertyType : 'text';
    return change(dbId, (db) => {
      if (db.properties.length >= MAX_PROPERTIES) return;
      const prop = makeProperty(type, name); db.properties.push(prop);
      for (const view of db.views) view.visiblePropertyIds.push(prop.id);
      for (const row of db.rows) row.values[prop.id] = cleanValue(prop, null);
    });
  }
  function mdDatabasePropertyUpdate(dbId: unknown, propertyId: unknown, patch: unknown): MdDatabase | null {
    return change(dbId, (db) => {
      const prop = db.properties.find((p) => p.id === propertyId);
      if (!prop || !patch || typeof patch !== 'object') return;
      const p = patch as any;
      if (typeof p.name === 'string' && p.name.trim()) prop.name = text(p.name, 80);
      if (prop.type === 'number' && typeof p.format === 'string') prop.format = text(p.format, 30) || 'number';
      if (prop.type === 'formula' && typeof p.formula === 'string') prop.formula = p.formula.slice(0, 4000);
      if (prop.type === 'relation' && p.relation && validId(p.relation.databaseId)) {
        prop.relation = { databaseId: p.relation.databaseId, limit: p.relation.limit === 'one' ? 'one' : 'many' };
        if (validId(p.relation.reciprocalPropertyId)) prop.relation.reciprocalPropertyId = p.relation.reciprocalPropertyId;
      }
      if (prop.type === 'rollup' && p.rollup) prop.rollup = clone(p.rollup);
      if (prop.type === 'button' && p.button) prop.button = clone(p.button);
    });
  }
  const mdDatabasePropertyRename = (dbId: unknown, propertyId: unknown, name: unknown): MdDatabase | null => mdDatabasePropertyUpdate(dbId, propertyId, { name });

  function convertValue(oldProp: MdDatabaseProperty, nextProp: MdDatabaseProperty, value: unknown): unknown {
    if (nextProp.type === oldProp.type) return cleanValue(nextProp, value);
    if (nextProp.type === 'checkbox') return !isEmpty(value) && value !== false && value !== 0;
    if (nextProp.type === 'number') return cleanValue(nextProp, typeof value === 'boolean' ? (value ? 1 : 0) : value);
    if (nextProp.type === 'multi') return cleanValue(nextProp, Array.isArray(value) ? value : value == null ? [] : [value]);
    if (nextProp.type === 'person') return Array.isArray(value) ? value.map(String) : isEmpty(value) ? [] : [String(value)];
    if (nextProp.type === 'relation' || nextProp.type === 'files') return [];
    if (COMPUTED.has(nextProp.type)) return null;
    if (oldProp.type === 'select' || oldProp.type === 'status') return String((oldProp.options || []).find((o) => o.id === value)?.name || '');
    if (oldProp.type === 'multi') return (Array.isArray(value) ? value : []).map((v) => (oldProp.options || []).find((o) => o.id === v)?.name || '').filter(Boolean).join(', ');
    return isEmpty(value) ? '' : String(value);
  }
  function mdDatabasePropertySetType(dbId: unknown, propertyId: unknown, typeValue: unknown): MdDatabase | null {
    if (propertyId === 'title' || !TYPES.includes(typeValue as MdDatabasePropertyType) || typeValue === 'title') return mdDatabaseRead(dbId);
    return change(dbId, (db) => {
      const index = db.properties.findIndex((p) => p.id === propertyId); if (index < 0) return;
      const oldProp = db.properties[index]!, next = { ...makeProperty(typeValue as MdDatabasePropertyType, oldProp.name), id: oldProp.id };
      if (['select', 'multi', 'status'].includes(next.type)) {
        const names = [...new Set(db.rows.flatMap((row) => {
          const raw = row.values[oldProp.id];
          if (oldProp.options) return (Array.isArray(raw) ? raw : [raw]).map((value) => oldProp.options!.find((o) => o.id === value)?.name || '').filter(Boolean);
          return Array.isArray(raw) ? raw.map(String) : isEmpty(raw) ? [] : [String(raw)];
        }).map((name) => name.trim()).filter(Boolean))].slice(0, 120);
        next.options = names.map((name, optionIndex) => ({ id: id('o'), name,
          color: COLORS[optionIndex % COLORS.length]! }));
      }
      db.properties[index] = next;
      for (const row of db.rows) {
        const raw = row.values[next.id];
        if (next.options) {
          const rawNames = oldProp.options ? (Array.isArray(raw) ? raw : [raw]).map((value) => oldProp.options!.find((o) => o.id === value)?.name || '')
            : Array.isArray(raw) ? raw.map(String) : isEmpty(raw) ? [] : [String(raw)];
          const ids = rawNames.map((name) => next.options!.find((o) => o.name === name)?.id).filter((value): value is string => !!value);
          row.values[next.id] = next.type === 'multi' ? ids : ids[0] || null;
        } else row.values[next.id] = convertValue(oldProp, next, raw);
      }
      for (const view of db.views) {
        view.sorts = view.sorts.filter((s) => s.propertyId !== next.id);
        view.filters = view.filters.filter((f) => f.propertyId !== next.id);
        if (view.groupBy === next.id) view.groupBy = null;
        if (view.datePropertyId === next.id) view.datePropertyId = null;
        if (view.endDatePropertyId === next.id) view.endDatePropertyId = null;
      }
    });
  }
  function mdDatabasePropertyDuplicate(dbId: unknown, propertyId: unknown): MdDatabase | null {
    return change(dbId, (db) => {
      const source = db.properties.find((p) => p.id === propertyId); if (!source || source.id === 'title' || db.properties.length >= MAX_PROPERTIES) return;
      const copy = clone(source); copy.id = id('p'); copy.name = `${source.name} copy`.slice(0, 80); db.properties.push(copy);
      for (const view of db.views) view.visiblePropertyIds.push(copy.id);
      for (const row of db.rows) row.values[copy.id] = clone(row.values[source.id]);
    });
  }
  function mdDatabasePropertyRemove(dbId: unknown, propertyId: unknown): MdDatabase | null {
    if (propertyId === 'title') return mdDatabaseRead(dbId);
    const removed = mdDatabaseRead(dbId)?.properties.find((p) => p.id === propertyId);
    const saved = change(dbId, (db) => {
      db.properties = db.properties.filter((p) => p.id !== propertyId);
      /* Any half of a mirrored pair living in this same database — a
         self-relation — loses its partner here, in the same draft, so one
         removal stays one undo. */
      for (const prop of db.properties) {
        const pair = prop.relation;
        if (prop.type === 'relation' && pair && pair.reciprocalPropertyId && pair.reciprocalPropertyId === propertyId) {
          prop.relation = { databaseId: pair.databaseId, limit: pair.limit };
        }
      }
      for (const row of db.rows) delete row.values[String(propertyId)];
      for (const view of db.views) {
        view.visiblePropertyIds = view.visiblePropertyIds.filter((p) => p !== propertyId);
        view.sorts = view.sorts.filter((s) => s.propertyId !== propertyId);
        view.filters = view.filters.filter((f) => f.propertyId !== propertyId);
        if (view.groupBy === propertyId) view.groupBy = null;
        if (view.calculations) delete view.calculations[String(propertyId)];
      }
      for (const prop of db.properties) {
        if (prop.type === 'rollup' && (prop.rollup?.relationPropertyId === propertyId || prop.rollup?.targetPropertyId === propertyId)) prop.rollup = undefined;
      }
    });
    /* The far half, in another database, reverts to an honest one-way relation
       and keeps its data — every row it names is still there. Left claiming a
       reciprocalPropertyId that no longer exists it would report itself as
       two-way while behaving as one-way, and mdDatabaseRelationMirror, which
       short-circuits on a truthy reciprocalPropertyId, would never repair it —
       re-mirroring from this side would mint a second column instead. */
    const gone = removed?.relation;
    if (saved && removed?.type === 'relation' && gone?.reciprocalPropertyId && gone.databaseId !== saved.id) {
      change(gone.databaseId, (target) => {
        const far = target.properties.find((p) => p.id === gone.reciprocalPropertyId);
        if (far?.relation) far.relation = { databaseId: far.relation.databaseId, limit: far.relation.limit };
      });
    }
    return saved;
  }
  function mdDatabasePropertyMove(dbId: unknown, propertyId: unknown, targetIndex: unknown): MdDatabase | null {
    return change(dbId, (db) => {
      const from = db.properties.findIndex((prop) => prop.id === propertyId);
      if (from < 0 || propertyId === 'title') return;
      const to = Math.max(1, Math.min(db.properties.length - 1, Math.floor(finite(targetIndex, from))));
      const [moved] = db.properties.splice(from, 1); if (!moved) return; db.properties.splice(to, 0, moved);
      for (const view of db.views) {
        const order = view.visiblePropertyIds.filter((id) => id !== propertyId);
        const previousId = db.properties[to - 1]?.id, anchor = previousId ? order.indexOf(previousId) + 1 : 0;
        order.splice(Math.max(0, anchor), 0, String(propertyId)); view.visiblePropertyIds = order;
      }
    });
  }
  function mdDatabasePropertyInsert(dbId: unknown, typeValue: unknown, name: unknown, anchorId: unknown, side: unknown): MdDatabase | null {
    const type = TYPES.includes(typeValue as MdDatabasePropertyType) && typeValue !== 'title' ? typeValue as MdDatabasePropertyType : 'text';
    return change(dbId, (db) => {
      if (db.properties.length >= MAX_PROPERTIES) return;
      const anchor = db.properties.findIndex((prop) => prop.id === anchorId), index = anchor < 0 ? db.properties.length : anchor + (side === 'left' ? 0 : 1);
      const prop = makeProperty(type, name); db.properties.splice(Math.max(1, index), 0, prop);
      for (const view of db.views) {
        const visibleAnchor = view.visiblePropertyIds.indexOf(String(anchorId));
        view.visiblePropertyIds.splice(visibleAnchor < 0 ? view.visiblePropertyIds.length : visibleAnchor + (side === 'left' ? 0 : 1), 0, prop.id);
      }
      for (const row of db.rows) row.values[prop.id] = cleanValue(prop, null);
    });
  }
  function mdDatabaseViewPropertyMove(dbId: unknown, viewId: unknown, propertyId: unknown, targetIndex: unknown): MdDatabase | null {
    return change(dbId, (db) => {
      const view = db.views.find((item) => item.id === viewId); if (!view) return;
      const from = view.visiblePropertyIds.indexOf(String(propertyId)); if (from < 0) return;
      const to = Math.max(0, Math.min(view.visiblePropertyIds.length - 1, Math.floor(finite(targetIndex, from))));
      const [moved] = view.visiblePropertyIds.splice(from, 1); if (moved) view.visiblePropertyIds.splice(to, 0, moved);
    });
  }
  function mdDatabaseOptionAdd(dbId: unknown, propertyId: unknown, name: unknown): MdDatabase | null {
    const clean = text(name, 80); if (!clean) return mdDatabaseRead(dbId);
    return change(dbId, (db) => { const prop = db.properties.find((p) => p.id === propertyId);
      if (!prop || !['select', 'multi', 'status'].includes(prop.type)) return;
      prop.options = prop.options || []; if (!prop.options.some((o) => o.name.toLocaleLowerCase() === clean.toLocaleLowerCase())) prop.options.push({ id: id('o'), name: clean, color: COLORS[prop.options.length % COLORS.length]! });
    });
  }
  function mdDatabaseOptionUpdate(dbId: unknown, propertyId: unknown, optionId: unknown, patch: unknown): MdDatabase | null {
    return change(dbId, (db) => { const prop = db.properties.find((p) => p.id === propertyId), option = prop?.options?.find((o) => o.id === optionId);
      if (!option || !patch || typeof patch !== 'object') return; const p = patch as any;
      if (typeof p.name === 'string' && p.name.trim()) option.name = text(p.name, 80);
      if (COLORS.includes(p.color)) option.color = p.color;
    });
  }
  function mdDatabaseOptionRemove(dbId: unknown, propertyId: unknown, optionId: unknown): MdDatabase | null {
    return change(dbId, (db) => { const prop = db.properties.find((p) => p.id === propertyId); if (!prop?.options) return;
      prop.options = prop.options.filter((o) => o.id !== optionId);
      for (const row of db.rows) row.values[prop.id] = prop.type === 'multi' ? (Array.isArray(row.values[prop.id]) ? row.values[prop.id] as unknown[] : []).filter((x) => x !== optionId) : row.values[prop.id] === optionId ? null : row.values[prop.id];
    });
  }

  /* `seed` is the one parameter in this family typed as its shape rather than
     as `unknown`, and a default only fires for `undefined` — so an explicit
     null, which is what a caller with nothing to seed most naturally reaches
     for, read straight through the default and threw on seed[prop.id]. Every
     sibling here takes `unknown` and validates; this one now does too. */
  function mdDatabaseRowAdd(dbId: unknown, seedValue: unknown = {}): MdDatabase | null {
    /* A row in the Main DB is a record kept elsewhere; a row in Omana People is
       a person in the registry, added by a form that posts to the server. */
    if (isShipped(dbId)) return null;
    const seed: Record<string, unknown> = seedValue && typeof seedValue === 'object' && !Array.isArray(seedValue)
      ? seedValue as Record<string, unknown> : {};
    let addedId = '';
    let saved = change(dbId, (db) => { if (db.rows.length >= MAX_ROWS) return;
      const now = Date.now(), values: Record<string, unknown> = {};
      for (const prop of db.properties) values[prop.id] = cleanValue(prop, seed[prop.id]);
      addedId = id('r'); db.rows.push({ id: addedId, values, body: '', created: now, updated: now, createdBy: 'You', updatedBy: 'You' });
    });
    if (!saved || !addedId) return saved;
    for (const automation of saved.automations || []) if (automation.enabled && automation.trigger === 'row_added') {
      if (automation.action === 'set_property' && automation.actionPropertyId) {
        saved = mdDatabaseCellSet(saved.id, addedId, automation.actionPropertyId, automation.value) || saved;
      } else if (automation.action === 'add_row') saved = change(saved.id, (db) => {
        if (db.rows.length >= MAX_ROWS) return; const now = Date.now(), values: Record<string, unknown> = {};
        for (const prop of db.properties) values[prop.id] = cleanValue(prop, null);
        db.rows.push({ id: id('r'), values, body: '', created: now, updated: now, createdBy: 'You', updatedBy: 'You' });
      }) || saved;
    }
    return saved;
  }
  function mdDatabaseRowMove(dbId: unknown, rowId: unknown, targetIndex: unknown): MdDatabase | null {
    return change(dbId, (db) => {
      const from = db.rows.findIndex((row) => row.id === rowId); if (from < 0) return;
      const to = Math.max(0, Math.min(db.rows.length - 1, Math.floor(finite(targetIndex, from))));
      const [moved] = db.rows.splice(from, 1); if (moved) db.rows.splice(to, 0, moved);
    });
  }
  function mdDatabaseRowMetaUpdate(dbId: unknown, rowId: unknown, patch: unknown): MdDatabase | null {
    return change(dbId, (db) => {
      const row = db.rows.find((item) => item.id === rowId); if (!row || !patch || typeof patch !== 'object') return;
      const p = patch as any;
      if ('icon' in p) row.icon = text(p.icon, 80) || undefined;
      if ('cover' in p) row.cover = typeof p.cover === 'string' ? p.cover.slice(0, 4000) || undefined : undefined;
      if ('favorite' in p) row.favorite = !!p.favorite;
      row.updated = Date.now(); row.updatedBy = 'You';
    });
  }
  function mdDatabaseRowCommentAdd(dbId: unknown, rowId: unknown, body: unknown): MdDatabase | null {
    const clean = typeof body === 'string' ? body.trim().slice(0, 10000) : ''; if (!clean) return mdDatabaseRead(dbId);
    return change(dbId, (db) => { const row = db.rows.find((item) => item.id === rowId); if (!row) return;
      row.comments = row.comments || []; row.comments.push({ id: id('c'), author: 'You', body: clean, created: Date.now() });
      row.updated = Date.now(); row.updatedBy = 'You';
    });
  }
  function mdDatabaseRowCommentRemove(dbId: unknown, rowId: unknown, commentId: unknown): MdDatabase | null {
    return change(dbId, (db) => { const row = db.rows.find((item) => item.id === rowId); if (row?.comments) row.comments = row.comments.filter((comment) => comment.id !== commentId); });
  }
  function mdDatabaseCellSet(dbId: unknown, rowId: unknown, propertyId: unknown, value: unknown): MdDatabase | null {
    const before = mdDatabaseRead(dbId), beforeProp = before?.properties.find((p) => p.id === propertyId), beforeRow = before?.rows.find((r) => r.id === rowId);
    const previous = beforeProp?.type === 'relation' && Array.isArray(beforeRow?.values[String(propertyId)]) ? beforeRow!.values[String(propertyId)] as string[] : [];
    let saved = change(dbId, (db) => { const row = db.rows.find((r) => r.id === rowId), prop = db.properties.find((p) => p.id === propertyId);
      if (!row || !prop || COMPUTED.has(prop.type)) return; row.values[prop.id] = cleanValue(prop, value); row.updated = Date.now(); row.updatedBy = 'You';
    });
    const prop = saved?.properties.find((p) => p.id === propertyId), relation = prop?.relation;
    if (saved && validId(rowId)) for (const automation of saved.automations || []) {
      if (!automation.enabled || automation.trigger !== 'property_changed' || automation.triggerPropertyId !== propertyId
        || automation.action !== 'set_property' || !automation.actionPropertyId || automation.actionPropertyId === propertyId) continue;
      const automated = change(saved.id, (db) => { const row = db.rows.find((item) => item.id === rowId), target = db.properties.find((item) => item.id === automation.actionPropertyId);
        if (row && target && !COMPUTED.has(target.type)) { row.values[target.id] = cleanValue(target, automation.value); row.updated = Date.now(); row.updatedBy = 'You'; }
      });
      if (automated) saved = automated;
    }
    if (saved && prop?.type === 'relation' && relation?.reciprocalPropertyId && validId(rowId)) {
      const currentRow = saved.rows.find((r) => r.id === rowId), next = Array.isArray(currentRow?.values[prop.id]) ? currentRow!.values[prop.id] as string[] : [];
      change(relation.databaseId, (target) => {
        const reciprocal = target.properties.find((p) => p.id === relation.reciprocalPropertyId); if (!reciprocal) return;
        for (const targetRow of target.rows) {
          const linked = Array.isArray(targetRow.values[reciprocal.id]) ? targetRow.values[reciprocal.id] as string[] : [];
          if (next.includes(targetRow.id) && !linked.includes(rowId)) targetRow.values[reciprocal.id] = reciprocal.relation?.limit === 'one' ? [rowId] : [...linked, rowId];
          else if (previous.includes(targetRow.id) && !next.includes(targetRow.id)) targetRow.values[reciprocal.id] = linked.filter((id) => id !== rowId);
        }
      });
    }
    return saved;
  }

  function mdDatabaseRelationMirror(dbId: unknown, propertyId: unknown, name?: unknown): MdDatabase | null {
    const source = mdDatabaseRead(dbId), sourceProp = source?.properties.find((p) => p.id === propertyId);
    if (!source || sourceProp?.type !== 'relation' || !sourceProp.relation) return source || null;
    if (sourceProp.relation.reciprocalPropertyId) return source;
    let reciprocalId = '';
    change(sourceProp.relation.databaseId, (target) => {
      const reciprocal = makeProperty('relation', text(name, 80) || source.name);
      reciprocal.relation = { databaseId: source.id, limit: 'many', reciprocalPropertyId: sourceProp.id };
      reciprocalId = reciprocal.id; target.properties.push(reciprocal);
      for (const view of target.views) view.visiblePropertyIds.push(reciprocal.id);
      for (const row of target.rows) row.values[reciprocal.id] = [];
      for (const sourceRow of source.rows) for (const targetRowId of Array.isArray(sourceRow.values[sourceProp.id]) ? sourceRow.values[sourceProp.id] as string[] : []) {
        const targetRow = target.rows.find((row) => row.id === targetRowId);
        if (targetRow) targetRow.values[reciprocal.id] = [...(targetRow.values[reciprocal.id] as string[]), sourceRow.id];
      }
    });
    return reciprocalId ? mdDatabasePropertyUpdate(source.id, sourceProp.id, { relation: { ...sourceProp.relation, reciprocalPropertyId: reciprocalId } }) : source;
  }
  function mdDatabaseRowBodySet(dbId: unknown, rowId: unknown, body: unknown): MdDatabase | null {
    return change(dbId, (db) => { const row = db.rows.find((r) => r.id === rowId); if (!row || typeof body !== 'string') return;
      row.body = body.slice(0, 500000); row.updated = Date.now(); row.updatedBy = 'You'; });
  }
  /* A mirrored relation is symmetric, and mdDatabaseCellSet carries a whole
     block to keep it that way whenever a cell changes. Removal owes the same
     debt and never paid it: the far side goes on holding a deleted row's id,
     and nothing else in the file will ever clear it. What the person sees is
     the 'Missing row' pill DbRelationCell falls back to — sitting in a cell
     that belongs to the OTHER database, which is the one place the person who
     deleted the row would never think to look.

     Two halves, because a relation can point anywhere:
       - within this same database (a self-relation holds both halves here),
         folded into the removal's own draft so one delete stays one undo;
       - into another database, cleaned with its own change() — the same shape
         cell edits already use, and the same history behaviour. */
  function unlinkSameDatabase(draft: MdDatabase, removed: Set<string>): void {
    for (const prop of draft.properties) {
      if (prop.type !== 'relation' || prop.relation?.databaseId !== draft.id) continue;
      for (const row of draft.rows) {
        const held = Array.isArray(row.values[prop.id]) ? row.values[prop.id] as string[] : [];
        if (held.some((id) => removed.has(id))) row.values[prop.id] = held.filter((id) => !removed.has(id));
      }
    }
  }
  function unlinkFarSide(before: MdDatabase, removed: Set<string>): void {
    for (const prop of before.properties) {
      const relation = prop.relation;
      if (prop.type !== 'relation' || !relation?.reciprocalPropertyId || relation.databaseId === before.id) continue;
      const touched = new Set<string>();
      for (const row of before.rows) {
        if (!removed.has(row.id)) continue;
        for (const id of Array.isArray(row.values[prop.id]) ? row.values[prop.id] as string[] : []) touched.add(id);
      }
      if (!touched.size) continue;
      change(relation.databaseId, (target) => {
        const reciprocal = target.properties.find((p) => p.id === relation.reciprocalPropertyId);
        if (!reciprocal) return;
        for (const targetRow of target.rows) {
          if (!touched.has(targetRow.id)) continue;
          const held = Array.isArray(targetRow.values[reciprocal.id]) ? targetRow.values[reciprocal.id] as string[] : [];
          if (held.some((id) => removed.has(id))) targetRow.values[reciprocal.id] = held.filter((id) => !removed.has(id));
        }
      });
    }
  }
  const mdDatabaseRowRemove = (dbId: unknown, rowId: unknown): MdDatabase | null => {
    const before = mdDatabaseRead(dbId), removed = new Set([String(rowId)]);
    const saved = change(dbId, (db) => { db.rows = db.rows.filter((r) => r.id !== rowId); unlinkSameDatabase(db, removed); });
    if (saved && before) unlinkFarSide(before, removed);
    return saved;
  };
  function mutationFailure(): MdDatabaseMutationResult {
    const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
    return { ok: false, reason: fault?.quota ? 'storage_full' : 'invalid' };
  }
  function mdDatabaseTryRowAdd(dbId: unknown, seed: Record<string, unknown> = {}): MdDatabaseMutationResult {
    const db = mdDatabaseRead(dbId);
    if (!db) return { ok: false, reason: 'missing' };
    if (db.locked) return { ok: false, reason: 'locked' };
    if (isShipped(db.id)) return { ok: false, reason: 'invalid' };
    if (db.rows.length >= MAX_ROWS || !seed || typeof seed !== 'object' || Array.isArray(seed)) return { ok: false, reason: 'invalid' };
    const saved = mdDatabaseRowAdd(db.id, seed);
    return saved ? { ok: true, value: saved, changed: true } : mutationFailure();
  }
  function mdDatabaseTryCellSet(dbId: unknown, rowId: unknown, propertyId: unknown, value: unknown): MdDatabaseMutationResult {
    const db = mdDatabaseRead(dbId);
    if (!db) return { ok: false, reason: 'missing' };
    if (db.locked) return { ok: false, reason: 'locked' };
    /* Omana People is read-only, refused HERE rather than by accident further
       down. Its rows are a projection of a table on the server: there is no
       document under a cell to write to, and an edge minted against one would
       be this database holding its own opinion about a person the registry
       owns. The Main DB is deliberately not in this refusal — filing a record
       from the catalogue is one of the things it is for. */
    if (isOmana(db.id)) return { ok: false, reason: 'invalid' };
    const row = db.rows.find((item) => item.id === rowId), prop = db.properties.find((item) => item.id === propertyId);
    if (!prop) return { ok: false, reason: 'missing' };
    /* Asked once, here, rather than beside each of the three writes below —
       the answer does not depend on which kind of row this is, and one guard is
       harder to forget than three. Before the row lookup's own refusals, so a
       value that could never be stored is never the thing that mints an edge. */
    if (!representable(prop, value, cleanValue(prop, value))) return { ok: false, reason: 'invalid' };
    /* A member's cell: the same door, a different home. The value is cleaned
       by the SAME rules as every native cell, then written to the membership
       entry — the database's data about the record. The title is the record's
       own and cannot be shadow-copied here: two names for one record is the
       duplication this phase exists to end. Relations and files carry side
       effects (mirroring, attachment transactions) that assume a native row,
       so a member refuses them rather than half-doing them. */
    if (!row) {
      /* TWO WAYS IN. An ordinary member row is named by its edge document, so
         the row id IS the edge id and one lookup finds it. A Main DB row has no
         edge at all until this moment: it is named by its ref key, and the edge
         is minted right here — the first time a cell the user added is actually
         filled. That is what keeps a catalogue of six thousand records at zero
         edges, and it is why the lookup below is by ref key there. */
      const virtual = isLibrarian(db.id);
      let entry = virtual
        ? (g.mdMembershipList ? g.mdMembershipList(db.id).find((item: any) => item.k === rowId) : null)
        : (g.mdMembershipEntry ? g.mdMembershipEntry(db.id, rowId) : null);
      const key = entry ? entry.k : rowId;
      /* No edge and no record behind the key is a row naming nothing. Asked
         before anything is written, so a refusal never leaves an edge behind. */
      if (!entry && !(virtual && typeof key === 'string' && g.mdRecordResolve && g.mdRecordResolve(key)))
        return { ok: false, reason: 'missing' };
      /* `locked` has meant a whole database until now; a member row is where it
         also comes to mean one RECORD. IQAMA ships as records the app defines,
         and a column added here must not become a way to edit them. The refusal
         is the same word for the same reason, so every surface that already
         handles it needs no new case. */
      if (g.mdRecordLocked && g.mdRecordLocked(key)) return { ok: false, reason: 'locked' };
      /* The record's own cells. Two of them — the name and the kind — are the
         record's to state and nobody else's, and the parent is walked rather
         than written; mdLibrarianCellSet refuses all three. The project cell is
         FORWARDED: it is editable here and writes the record's own home, never
         an edge, which is the two-kinds-of-cell line drawn in code. No edge is
         minted on this path, so filing a record from the catalogue still costs
         zero storage documents. */
      if (g.mdLibrarianOwns && g.mdLibrarianOwns(db.id, prop.id) && g.mdLibrarianCellSet) {
        /* Read where the cell stands BEFORE writing it, so the undo stack can
           hold the one thing this path has no snapshot of. Filing a record used
           to be the single edit in this database that Ctrl+Z could not take
           back — the write landed in the record's home, and nothing here knew
           what had been there. */
        const before = typeof g.mdLibrarianCellRead === 'function'
          ? g.mdLibrarianCellRead(key, prop.id) : null;
        const wrote = g.mdLibrarianCellSet(key, prop.id, cleanValue(prop, value));
        if (!wrote.ok) return { ok: false, reason: 'invalid' };
        if (!wrote.changed) return { ok: true, value: db, changed: false };
        if (before !== null) pushHistory(db.id, { kind: 'record', key: key as string, propertyId: prop.id, value: before });
        /* The database document did not change; the record did. A fresh read is
           still a fresh object, and the rows inside it are recomputed — which is
           what makes this notify say something to the table. */
        const fresh = mdDatabaseRead(db.id)!;
        notify(db.id, fresh);
        return { ok: true, value: fresh, changed: true };
      }
      /* The same line for every other database: a member row's title is the
         record's own, and this is also the refusal that stands when the
         librarian module is not loaded at all. */
      if (prop.id === 'title' || (g.mdLibrarianOwns && g.mdLibrarianOwns(db.id, prop.id))
        || COMPUTED.has(prop.type) || prop.type === 'relation' || prop.type === 'files') return { ok: false, reason: 'invalid' };
      const next = cleanValue(prop, value);
      if (JSON.stringify((entry ? entry.v[prop.id] : undefined) ?? null) === JSON.stringify(next ?? null)) return { ok: true, value: db, changed: false };
      /* Clearing a cell that was never filled is not an edit. Without this, a
         click into a cell and back out would mint an edge holding nothing. */
      if (!entry && (next === '' || next === false || (Array.isArray(next) && !next.length))) return { ok: true, value: db, changed: false };
      const membershipBefore = membershipNow(db.id);
      if (!entry) {
        if (!g.mdMembershipAdd || !g.mdMembershipAdd(db.id, key)) return mutationFailure();
        entry = g.mdMembershipList(db.id).find((item: any) => item.k === key);
        if (!entry) return mutationFailure();
      }
      if (!g.mdMembershipCellSet(db.id, entry.id, prop.id, next)) return mutationFailure();
      recordMembershipHistory(db.id, membershipBefore);
      const fresh = mdDatabaseRead(db.id)!;
      notify(db.id, fresh);
      return { ok: true, value: fresh, changed: true };
    }
    if (COMPUTED.has(prop.type)) return { ok: false, reason: 'invalid' };
    const next = cleanValue(prop, value);
    if (JSON.stringify(row.values[prop.id]) === JSON.stringify(next)) return { ok: true, value: db, changed: false };
    const saved = mdDatabaseCellSet(db.id, row.id, prop.id, next);
    return saved ? { ok: true, value: saved, changed: true } : mutationFailure();
  }
  function mdDatabaseTryCellsSet(dbId: unknown, edits: unknown): MdDatabaseMutationResult {
    const db = mdDatabaseRead(dbId);
    if (!db) return { ok: false, reason: 'missing' };
    if (db.locked) return { ok: false, reason: 'locked' };
    if (!Array.isArray(edits) || edits.length > MAX_ROWS * MAX_PROPERTIES) return { ok: false, reason: 'invalid' };

    const rowsById = new Map(db.rows.map((row) => [row.id, row]));
    const propertiesById = new Map(db.properties.map((property) => [property.id, property]));
    const unique = new Map<string, MdDatabaseCellEdit & { next: unknown }>();
    for (const raw of edits) {
      if (!raw || typeof raw !== 'object') return { ok: false, reason: 'invalid' };
      const edit = raw as MdDatabaseCellEdit;
      const row = rowsById.get(edit.rowId), property = propertiesById.get(edit.propertyId);
      if (!row || !property) return { ok: false, reason: 'missing' };
      // Relations have reciprocal side effects and files need an attachment
      // transaction; neither belongs in a rectangular clipboard operation.
      if (COMPUTED.has(property.type) || property.type === 'relation' || property.type === 'files') {
        return { ok: false, reason: 'invalid' };
      }
      /* The same promise the single-cell door makes, kept by the batch one. A
         paste refuses whole rather than per-cell, which is already how this
         function treats a relation or a computed column in the rectangle: one
         `ok: false` for the operation, nothing written. Silently nulling the
         cells it could not read would be the worst of it — the paste would look
         like it worked and the holes would be found weeks later. */
      const next = cleanValue(property, edit.value);
      if (!representable(property, edit.value, next)) return { ok: false, reason: 'invalid' };
      unique.set(`${row.id}:${property.id}`, { ...edit, next });
    }
    const changed = [...unique.values()].filter((edit) => {
      const row = rowsById.get(edit.rowId)!;
      return JSON.stringify(row.values[edit.propertyId]) !== JSON.stringify(edit.next);
    });
    if (!changed.length) return { ok: true, value: db, changed: false };

    const now = Date.now();
    const saved = change(db.id, (draft) => {
      const draftRows = new Map(draft.rows.map((row) => [row.id, row]));
      const draftProperties = new Map(draft.properties.map((property) => [property.id, property]));
      for (const edit of changed) {
        const row = draftRows.get(edit.rowId)!;
        row.values[edit.propertyId] = edit.next;
        row.updated = now; row.updatedBy = 'You';
        for (const automation of draft.automations || []) {
          if (!automation.enabled || automation.trigger !== 'property_changed'
            || automation.triggerPropertyId !== edit.propertyId || automation.action !== 'set_property'
            || !automation.actionPropertyId || automation.actionPropertyId === edit.propertyId) continue;
          const target = draftProperties.get(automation.actionPropertyId);
          if (target && !COMPUTED.has(target.type) && target.type !== 'relation' && target.type !== 'files') {
            row.values[target.id] = cleanValue(target, automation.value);
          }
        }
      }
    });
    return saved ? { ok: true, value: saved, changed: true } : mutationFailure();
  }
  function mdDatabaseTryRowRemove(dbId: unknown, rowId: unknown): MdDatabaseMutationResult {
    const db = mdDatabaseRead(dbId);
    if (!db || !db.rows.some((row) => row.id === rowId)) return { ok: false, reason: 'missing' };
    if (db.locked) return { ok: false, reason: 'locked' };
    const saved = mdDatabaseRowRemove(db.id, rowId);
    return saved ? { ok: true, value: saved, changed: true } : mutationFailure();
  }
  /* A record already written joins a database — the first thing a person can
     actually DO with this phase. A membership is written, never a copy; adding
     a record that is already here changes nothing and says so. */
  function mdDatabaseTryMemberAdd(dbId: unknown, refKey: unknown): MdDatabaseMutationResult {
    const db = mdDatabaseRead(dbId);
    if (!db) return { ok: false, reason: 'missing' };
    if (db.locked) return { ok: false, reason: 'locked' };
    if (!g.mdMembershipAdd || typeof refKey !== 'string' || !g.mdRecordRefParse || !g.mdRecordRefParse(refKey)) return { ok: false, reason: 'invalid' };
    const already = typeof g.mdMembershipFind === 'function'
      ? !!g.mdMembershipFind(db.id, refKey)
      : g.mdMembershipList(db.id).some((entry: { k: string }) => entry.k === refKey);
    if (already) return { ok: true, value: db, changed: false };
    const membershipBefore = membershipNow(db.id);
    if (!g.mdMembershipAdd(db.id, refKey)) return mutationFailure();
    recordMembershipHistory(db.id, membershipBefore);
    const fresh = mdDatabaseRead(db.id)!;
    notify(db.id, fresh);
    return { ok: true, value: fresh, changed: true };
  }
  function mdDatabaseTryRowsRemove(dbId: unknown, rowIds: unknown): MdDatabaseMutationResult {
    const db = mdDatabaseRead(dbId);
    if (!db) return { ok: false, reason: 'missing' };
    if (db.locked) return { ok: false, reason: 'locked' };
    if (!Array.isArray(rowIds)) return { ok: false, reason: 'invalid' };
    const ids = new Set(rowIds.filter(validId));
    if (ids.size !== rowIds.length) return { ok: false, reason: 'missing' };
    /* One sweep may take native rows and members together, and the two mean
       different things: a native row is DELETED; a member loses its MEMBERSHIP
       and the record it names keeps existing wherever it lives. That is the
       product promise of this phase, made literal here. */
    const native = new Set([...ids].filter((rowId) => db.rows.some((row) => row.id === rowId)));
    const members = [...ids].filter((rowId) => !native.has(rowId)
      && g.mdMembershipEntry && g.mdMembershipEntry(db.id, rowId));
    if (native.size + members.length !== ids.size) return { ok: false, reason: 'missing' };
    if (!ids.size) return { ok: true, value: db, changed: false };
    const membershipBefore = members.length ? membershipNow(db.id) : null;
    for (const rowId of members) g.mdMembershipRemoveByRow(db.id, rowId);
    if (!native.size) {
      if (membershipBefore) recordMembershipHistory(db.id, membershipBefore);
      const fresh = mdDatabaseRead(db.id)!;
      notify(db.id, fresh);
      return { ok: true, value: fresh, changed: true };
    }
    const saved = change(db.id, (draft) => { draft.rows = draft.rows.filter((row) => !native.has(row.id)); unlinkSameDatabase(draft, native); });
    if (saved && membershipBefore) {
      /* One sweep, one undo: change() just pushed the database snapshot; fold
         the membership snapshot into that same entry rather than stacking a
         second step the person never took. */
      const history = historyFor(db.id);
      const top = history.undo[history.undo.length - 1];
      if (top && top.kind === 'db') history.undo[history.undo.length - 1] = { kind: 'both', db: top.value, membership: membershipBefore };
    }
    /* After the fold, never before it: this walks OTHER databases' histories,
       and the entry being folded above has to stay the top of this one. */
    if (saved) unlinkFarSide(db, native);
    return saved ? { ok: true, value: saved, changed: true } : mutationFailure();
  }
  function mdDatabaseTryRowsDuplicate(dbId: unknown, rowIds: unknown): MdDatabaseMutationResult {
    const db = mdDatabaseRead(dbId);
    if (!db) return { ok: false, reason: 'missing' };
    if (db.locked) return { ok: false, reason: 'locked' };
    if (!Array.isArray(rowIds)) return { ok: false, reason: 'invalid' };
    const ids = new Set(rowIds.filter(validId));
    const source = db.rows.filter((row) => ids.has(row.id));
    if (ids.size !== rowIds.length || source.length !== ids.size) return { ok: false, reason: 'missing' };
    if (!source.length) return { ok: true, value: db, changed: false };
    if (db.rows.length + source.length > MAX_ROWS) return { ok: false, reason: 'invalid' };
    const now = Date.now();
    const saved = change(db.id, (draft) => {
      for (const sourceRow of source) {
        const copy = clone(sourceRow); copy.id = id('r'); copy.values.title = `${copy.values.title || 'Untitled'} copy`;
        copy.created = now; copy.updated = now; copy.createdBy = 'You'; copy.updatedBy = 'You';
        draft.rows.push(copy);
      }
    });
    return saved ? { ok: true, value: saved, changed: true } : mutationFailure();
  }
  function mdDatabaseRowDuplicate(dbId: unknown, rowId: unknown): MdDatabase | null {
    return change(dbId, (db) => { const row = db.rows.find((r) => r.id === rowId); if (!row || db.rows.length >= MAX_ROWS) return;
      const now = Date.now(), copy = clone(row); copy.id = id('r'); copy.values.title = `${copy.values.title || 'Untitled'} copy`; copy.created = now; copy.updated = now; copy.createdBy = 'You'; copy.updatedBy = 'You'; db.rows.push(copy); });
  }
  function mdDatabaseRowMoveTo(dbId: unknown, rowId: unknown, targetDatabaseId: unknown): boolean {
    const source = mdDatabaseRead(dbId), target = mdDatabaseRead(targetDatabaseId);
    const sourceRow = source?.rows.find((row) => row.id === rowId);
    if (!source || !target || !sourceRow || source.id === target.id) return false;
    const now = Date.now(), values: Record<string, unknown> = {};
    for (const targetProp of target.properties) {
      const sourceProp = targetProp.id === 'title' ? source.properties[0]
        : source.properties.find((prop) => prop.name.toLocaleLowerCase() === targetProp.name.toLocaleLowerCase() && prop.type === targetProp.type);
      values[targetProp.id] = cleanValue(targetProp, sourceProp ? sourceRow.values[sourceProp.id] : null);
    }
    const moved: MdDatabaseRow = { ...clone(sourceRow), id: id('r'), values, updated: now, updatedBy: 'You' };
    const saved = mdDatabaseSave({ ...target, rows: [...target.rows, moved] }); if (!saved) return false;
    return !!mdDatabaseRowRemove(source.id, sourceRow.id);
  }

  // Formula parser: small, deterministic and deliberately unable to execute JS.
  type Token = { kind: 'number' | 'string' | 'name' | 'op' | 'eof'; value: string };
  function tokens(source: string): Token[] {
    const out: Token[] = []; let at = 0;
    while (at < source.length) {
      const c = source[at]!; if (/\s/.test(c)) { at++; continue; }
      if (/\d/.test(c) || (c === '.' && /\d/.test(source[at + 1] || ''))) { const start = at++; while (/[\d.]/.test(source[at] || '')) at++; out.push({ kind: 'number', value: source.slice(start, at) }); continue; }
      if (c === '"' || c === "'") { const quote = c; let value = ''; at++; while (at < source.length && source[at] !== quote) { if (source[at] === '\\' && at + 1 < source.length) at++; value += source[at++]!; } at++; out.push({ kind: 'string', value }); continue; }
      if (/[A-Za-z_]/.test(c)) { const start = at++; while (/[A-Za-z0-9_]/.test(source[at] || '')) at++; out.push({ kind: 'name', value: source.slice(start, at) }); continue; }
      const pair = source.slice(at, at + 2); if (['==', '!=', '>=', '<=', '&&', '||'].includes(pair)) { out.push({ kind: 'op', value: pair }); at += 2; continue; }
      if ('+-*/%><(),!'.includes(c)) { out.push({ kind: 'op', value: c }); at++; continue; }
      throw new Error(`Unexpected “${c}”`);
    }
    out.push({ kind: 'eof', value: '' }); return out;
  }
  /* `extra` is how a formula reaches something the database does not hold.
     A member row names a RECORD, and a record can carry numbers of its own —
     the forty-day reading is the first — so the row's own answer is bound in
     here rather than the engine learning what a prayer is. The table below
     stays a table of arithmetic and text. */
  function formulaEvaluate(source: string, propertyValue: (name: string) => unknown,
    extra?: Record<string, (...args: unknown[]) => unknown>): unknown {
    const stream = tokens(source); let at = 0; const peek = () => stream[at]!, take = () => stream[at++]!;
    const truthy = (v: unknown) => !isEmpty(v) && v !== false && v !== 0;
    const number = (v: unknown) => Number.isFinite(Number(v)) ? Number(v) : 0;
    const funcs: Record<string, (...args: unknown[]) => unknown> = {
      prop: (name) => propertyValue(String(name)), if: (test, yes, no) => truthy(test) ? yes : no,
      empty: (v) => isEmpty(v), concat: (...args) => args.map((v) => v == null ? '' : String(v)).join(''),
      length: (v) => Array.isArray(v) || typeof v === 'string' ? v.length : 0,
      contains: (v, needle) => String(v ?? '').toLocaleLowerCase().includes(String(needle ?? '').toLocaleLowerCase()),
      lower: (v) => String(v ?? '').toLocaleLowerCase(), upper: (v) => String(v ?? '').toLocaleUpperCase(),
      format: (v) => String(v ?? ''), toNumber: number, round: (v, digits = 0) => { const p = 10 ** number(digits); return Math.round(number(v) * p) / p; },
      floor: (v) => Math.floor(number(v)), ceil: (v) => Math.ceil(number(v)), abs: (v) => Math.abs(number(v)),
      /* Nothing to compare is an ordinary state — an empty multi-select gets
         there — and Math.min()'s answer for it is Infinity, which is a sentinel
         from inside the maths library, not something to show a reader. Guarded
         the way average() one line down already guards the same case. */
      min: (...v) => { const n = v.flat().map(number); return n.length ? Math.min(...n) : null; },
      max: (...v) => { const n = v.flat().map(number); return n.length ? Math.max(...n) : null; },
      sum: (...v) => v.flat().reduce<number>((total, item) => total + number(item), 0),
      average: (...v) => { const values = v.flat().map(number); return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0; },
      /* The one numeric function that did not go through number(), and so the
         one that could put the literal text "NaN" in front of somebody who had
         filled in a start date and not yet an end date. An unparseable side is
         empty, which is what it actually is, and every calculation over the
         column then skips it instead of poisoning a sum. */
      dateBetween: (a, b, unit = 'days') => {
        const from = new Date(String(a)).getTime(), to = new Date(String(b)).getTime();
        if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
        const units: Record<string, number> = { minutes: 60000, hours: 3600000, days: 86400000, weeks: 604800000 };
        return Math.round((from - to) / (units[String(unit)] || 86400000));
      },
      now: () => new Date().toISOString(), today: () => new Date().toISOString().slice(0, 10),
      ...(extra || {}),
    };
    const primary = (): unknown => {
      const token = take();
      if (token.kind === 'number') return Number(token.value);
      if (token.kind === 'string') return token.value;
      if (token.kind === 'name') {
        if (token.value === 'true' || token.value === 'false') return token.value === 'true';
        if (token.value === 'null') return null;
        if (peek().value === '(') { take(); const args: unknown[] = []; if (peek().value !== ')') do { args.push(expression()); if (peek().value !== ',') break; take(); } while (true); if (take().value !== ')') throw new Error('Expected )'); const fn = funcs[token.value]; if (!fn) throw new Error(`Unknown function ${token.value}`); return fn(...args); }
        return propertyValue(token.value);
      }
      if (token.value === '(') { const value = expression(); if (take().value !== ')') throw new Error('Expected )'); return value; }
      if (token.value === '-' || token.value === '!' || token.value === '+') { const value = primary(); return token.value === '-' ? -number(value) : token.value === '!' ? !truthy(value) : number(value); }
      throw new Error('Expected value');
    };
    const binary = (next: () => unknown, operators: string[], apply: (op: string, a: unknown, b: unknown) => unknown): (() => unknown) => () => { let value = next(); while (operators.includes(peek().value)) { const op = take().value; value = apply(op, value, next()); } return value; };
    const multiply = binary(primary, ['*', '/', '%'], (op, a, b) => op === '*' ? number(a) * number(b) : op === '/' ? number(b) ? number(a) / number(b) : null : number(a) % number(b));
    const add = binary(multiply, ['+', '-'], (op, a, b) => op === '+' && (typeof a === 'string' || typeof b === 'string') ? String(a ?? '') + String(b ?? '') : op === '+' ? number(a) + number(b) : number(a) - number(b));
    const compare = binary(add, ['>', '<', '>=', '<=', '==', '!='], (op, a, b) => op === '>' ? (a as any) > (b as any) : op === '<' ? (a as any) < (b as any) : op === '>=' ? (a as any) >= (b as any) : op === '<=' ? (a as any) <= (b as any) : op === '==' ? a === b : a !== b);
    const logic = binary(compare, ['&&', '||'], (op, a, b) => op === '&&' ? truthy(a) && truthy(b) : truthy(a) || truthy(b));
    const expression = logic, result = expression(); if (peek().kind !== 'eof') throw new Error('Unexpected token'); return result;
  }

  function mdDatabaseValue(db: MdDatabase, row: MdDatabaseRow, propertyId: string, stack = new Set<string>()): unknown {
    const prop = db.properties.find((p) => p.id === propertyId); if (!prop) return null;
    const marker = `${db.id}:${row.id}:${prop.id}`; if (stack.has(marker)) return '#CYCLE!'; stack.add(marker);
    let result: unknown;
    if (prop.type === 'id') result = row.id;
    else if (prop.type === 'created_time') result = new Date(row.created).toISOString();
    else if (prop.type === 'updated_time') result = new Date(row.updated).toISOString();
    else if (prop.type === 'created_by') result = row.createdBy || 'You';
    else if (prop.type === 'updated_by') result = row.updatedBy || 'You';
    else if (prop.type === 'formula') {
      try { result = prop.formula ? formulaEvaluate(prop.formula, (name) => {
        const target = db.properties.find((p) => p.id === name || p.name.toLocaleLowerCase() === name.toLocaleLowerCase());
        if (!target) return null;
        const value = mdDatabaseValue(db, row, target.id, stack);
        if (target.type === 'select' || target.type === 'status') return target.options?.find((o) => o.id === value)?.name || '';
        if (target.type === 'multi') return (Array.isArray(value) ? value : []).map((id) => target.options?.find((o) => o.id === id)?.name || '').filter(Boolean);
        return value;
      }, {
        /* What this row's record says about itself. Blank on a native row, on a
           record that has no reading, and on a record that is gone — none of
           those is an error, and a column that shouted #ERROR at every ordinary
           row would be useless in a catalogue of everything. */
        reading: (part) => g.mdRecordReading ? g.mdRecordReading(row.member, part) : '',
      }) : null; } catch (error) { result = `#ERROR: ${error instanceof Error ? error.message : 'Formula'}`; }
    } else if (prop.type === 'rollup') {
      const rollup = prop.rollup, relation = db.properties.find((p) => p.id === rollup?.relationPropertyId);
      const targetDb = relation?.relation ? mdDatabaseReadCanonical(relation.relation.databaseId) : null;
      const linked = Array.isArray(row.values[relation?.id || '']) ? row.values[relation!.id] as string[] : [];
      const values = targetDb && rollup ? linked.flatMap((rid) => { const linkedRow = targetDb.rows.find((r) => r.id === rid); return linkedRow ? [mdDatabaseValue(targetDb, linkedRow, rollup.targetPropertyId, stack)] : []; }) : [];
      result = rollup?.calculation === 'show_original' ? values : mdDatabaseCalculate(values, rollup?.calculation || 'count');
    } else result = row.values[prop.id];
    stack.delete(marker); return result;
  }

  function mdDatabaseCalculate(valuesOrDb: unknown, calculationValue: unknown, propertyId?: unknown): unknown {
    let values: unknown[];
    if (Array.isArray(valuesOrDb)) values = valuesOrDb;
    else {
      const db = databaseForQuery(valuesOrDb); if (!db || typeof propertyId !== 'string') return null;
      values = db.rows.map((row) => mdDatabaseValue(db, row, propertyId));
    }
    const calculation = CALCULATIONS.includes(calculationValue as MdDatabaseCalculation) ? calculationValue as MdDatabaseCalculation : 'none';
    const present = values.filter((v) => !isEmpty(v)), nums = present.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    if (calculation === 'none') return null;
    if (calculation === 'count') return values.length;
    if (calculation === 'count_values') return present.length;
    if (calculation === 'count_unique') return new Set(present.map((v) => JSON.stringify(v))).size;
    if (calculation === 'percent_empty' || calculation === 'percent_not_empty') return values.length ? Math.round(100 * (calculation === 'percent_empty' ? values.length - present.length : present.length) / values.length) : 0;
    if (calculation === 'percent_checked' || calculation === 'percent_unchecked') return values.length ? Math.round(100 * values.filter((v) => calculation === 'percent_checked' ? v === true : v !== true).length / values.length) : 0;
    if (calculation === 'sum') return nums.reduce((a, b) => a + b, 0);
    if (calculation === 'average') return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
    if (calculation === 'median') return nums.length ? (nums[Math.floor((nums.length - 1) / 2)]! + nums[Math.ceil((nums.length - 1) / 2)]!) / 2 : null;
    if (calculation === 'min') return nums.length ? nums[0] : null;
    if (calculation === 'max') return nums.length ? nums[nums.length - 1] : null;
    if (calculation === 'range') return nums.length ? nums[nums.length - 1]! - nums[0]! : null;
    const dates = present.map((v) => new Date(String(v)).getTime()).filter(Number.isFinite).sort((a, b) => a - b);
    if (calculation === 'earliest') return dates.length ? new Date(dates[0]!).toISOString() : null;
    if (calculation === 'latest') return dates.length ? new Date(dates[dates.length - 1]!).toISOString() : null;
    if (calculation === 'date_range') return dates.length > 1 ? dates[dates.length - 1]! - dates[0]! : 0;
    return null;
  }

  function mdDatabaseViewAdd(dbId: unknown, typeValue: unknown): MdDatabase | null {
    const type = VIEW_TYPES.includes(typeValue as MdDatabaseViewType) ? typeValue as MdDatabaseViewType : 'table';
    return change(dbId, (db) => { if (db.views.length >= MAX_VIEWS) return;
      const group = db.properties.find((p) => p.type === 'status' || p.type === 'select');
      const date = db.properties.find((p) => p.type === 'date'), number = db.properties.find((p) => p.type === 'number' || p.type === 'formula');
      const view: MdDatabaseView = { id: id('v'), name: type[0]!.toUpperCase() + type.slice(1), type,
        visiblePropertyIds: db.properties.map((p) => p.id), sorts: [], filters: [], groupBy: ['board', 'chart'].includes(type) ? group?.id || null : null,
        groupDirection: 'asc', hiddenGroups: [], calculations: {}, datePropertyId: ['calendar', 'timeline', 'feed', 'schedule'].includes(type) ? date?.id || null : null,
        endDatePropertyId: null, chartPropertyId: type === 'chart' ? number?.id || null : null, chartType: 'bar',
        chartAggregation: type === 'chart' && number ? 'sum' : 'count',
        propertyWidths: {}, frozenPropertyIds: [], wrapPropertyIds: [], showDataSourceTitle: true,
        showVerticalLines: true, showPageIcon: true, wrapContent: true, openPagesIn: 'side_peek',
        conditionalColors: [], dashboardAggregations: {} };
      db.views.push(view); db.activeViewId = view.id;
    });
  }
  function mdDatabaseViewUpdate(dbId: unknown, viewId: unknown, patch: unknown): MdDatabase | null {
    return change(dbId, (db) => { const view = db.views.find((v) => v.id === viewId); if (!view || !patch || typeof patch !== 'object') return;
      const p = patch as any; if (typeof p.name === 'string' && p.name.trim()) view.name = text(p.name, 60);
      if (VIEW_TYPES.includes(p.type)) view.type = p.type;
      if (Array.isArray(p.visiblePropertyIds)) view.visiblePropertyIds = p.visiblePropertyIds;
      if (Array.isArray(p.sorts)) view.sorts = p.sorts;
      if (Array.isArray(p.filters)) view.filters = p.filters;
      if ('groupBy' in p && (p.groupBy === null || db.properties.some((prop) => prop.id === p.groupBy))) view.groupBy = p.groupBy;
      if (p.groupDirection === 'asc' || p.groupDirection === 'desc') view.groupDirection = p.groupDirection;
      if (Array.isArray(p.hiddenGroups)) view.hiddenGroups = p.hiddenGroups;
      if (p.calculations && typeof p.calculations === 'object') view.calculations = p.calculations;
      for (const key of ['datePropertyId', 'endDatePropertyId', 'chartPropertyId']) {
        if (key in p && (p[key] === null || db.properties.some((prop) => prop.id === p[key]))) (view as any)[key] = p[key];
      }
      if ((g.MD_TIMELINE_ZOOMS || []).includes(p.timelineZoom)) view.timelineZoom = p.timelineZoom;
      if (p.chartType === 'bar' || p.chartType === 'donut') view.chartType = p.chartType;
      if (CHART_AGGREGATIONS.includes(p.chartAggregation)) view.chartAggregation = p.chartAggregation;
      if (p.dashboardAggregations && typeof p.dashboardAggregations === 'object') {
        const next: Record<string, 'sum' | 'average' | 'median' | 'count'> = {};
        for (const [key, value] of Object.entries(p.dashboardAggregations)) {
          if (db.properties.some((prop) => prop.id === key) && DASHBOARD_AGGREGATIONS.includes(value as string)) next[key] = value as 'sum' | 'average' | 'median' | 'count';
        }
        view.dashboardAggregations = next;
      }
      if (p.propertyWidths && typeof p.propertyWidths === 'object') view.propertyWidths = p.propertyWidths;
      if (Array.isArray(p.frozenPropertyIds)) view.frozenPropertyIds = p.frozenPropertyIds;
      if (Array.isArray(p.wrapPropertyIds)) view.wrapPropertyIds = p.wrapPropertyIds;
      for (const key of ['showDataSourceTitle', 'showVerticalLines', 'showPageIcon', 'wrapContent']) {
        if (typeof p[key] === 'boolean') (view as any)[key] = p[key];
      }
      if (['side_peek', 'center_peek', 'full_page'].includes(p.openPagesIn)) view.openPagesIn = p.openPagesIn;
      if (Array.isArray(p.conditionalColors)) view.conditionalColors = p.conditionalColors;
    });
  }
  function mdDatabaseViewActivate(dbId: unknown, viewId: unknown): MdDatabase | null {
    return change(dbId, (db) => {
      if (db.views.some((view) => view.id === viewId)) db.activeViewId = String(viewId);
    });
  }
  function mdDatabaseViewRemove(dbId: unknown, viewId: unknown): MdDatabase | null {
    return change(dbId, (db) => { if (db.views.length < 2) return; db.views = db.views.filter((v) => v.id !== viewId); if (db.activeViewId === viewId) db.activeViewId = db.views[0]!.id; });
  }
  function mdDatabaseViewDuplicate(dbId: unknown, viewId: unknown): MdDatabase | null {
    return change(dbId, (db) => { const source = db.views.find((view) => view.id === viewId);
      if (!source || db.views.length >= MAX_VIEWS) return; const copy = clone(source); copy.id = id('v'); copy.name = `${source.name} copy`.slice(0, 60);
      db.views.splice(db.views.indexOf(source) + 1, 0, copy); db.activeViewId = copy.id;
    });
  }
  function comparable(value: unknown): string | number {
    if (typeof value === 'number') return value; if (typeof value === 'boolean') return value ? 1 : 0;
    if (Array.isArray(value)) return value.join(' '); return value == null ? '' : String(value).toLocaleLowerCase();
  }
  function compareForSort(a: unknown, b: unknown, prop: MdDatabaseProperty): number {
    const aEmpty = isEmpty(a), bEmpty = isEmpty(b);
    if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
    if (prop.type === 'select' || prop.type === 'status') {
      const rank = (value: unknown) => Math.max(0, (prop.options || []).findIndex((option) => option.id === value));
      return rank(a) - rank(b);
    }
    if (prop.type === 'multi') {
      const ranks = (value: unknown): number[] => (Array.isArray(value) ? value : [])
        .map((item) => (prop.options || []).findIndex((option) => option.id === item))
        .filter((index) => index >= 0).sort((left, right) => left - right);
      const left = ranks(a), right = ranks(b), length = Math.max(left.length, right.length);
      for (let index = 0; index < length; index++) {
        if (left[index] == null) return -1;
        if (right[index] == null) return 1;
        if (left[index] !== right[index]) return left[index]! - right[index]!;
      }
      return 0;
    }
    const av = comparable(a), bv = comparable(b);
    return typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
  }
  /** The operators that put two values in an order, as opposed to asking what a
      value is or what it contains. The only ones an empty cell must lose. */
  const ORDERING = new Set(['greater', 'greater_equal', 'less', 'less_equal',
    'before', 'after', 'on_or_before', 'on_or_after']);
  function passes(value: unknown, filter: MdDatabaseFilter): boolean {
    const empty = isEmpty(value); if (filter.operator === 'empty') return empty; if (filter.operator === 'not_empty') return !empty;
    if (filter.operator === 'checked') return value === true; if (filter.operator === 'unchecked') return value !== true;
    // An unfinished filter is no filter: every remaining operator takes a
    // value, and while that value is still empty the view must not collapse.
    if (filter.value == null || filter.value === '') return true;
    /* An empty cell has no position on a line. comparable() answers '' for it,
       '' loses against every string, and coerces to 0 against every number — so
       a date column with no date passed every `before`, and a number column
       with no number passed `greater_equal 0`. A view called "due before
       Friday" listed every row nobody had given a due date, and those rows look
       exactly like rows somebody merely forgot to fill in, which is why this
       could sit here being wrong without ever reading as wrong.

       Only the ordering operators. `is_not` keeps letting empties through on
       purpose: a status nobody has set genuinely is not Done. compareForSort
       above already treats emptiness as its own case; this is that rule, for
       filtering. */
    if (empty && ORDERING.has(filter.operator)) return false;
    const a = comparable(value), b = comparable(filter.value);
    if (filter.operator === 'contains') return String(a).includes(String(b));
    if (filter.operator === 'not_contains') return !String(a).includes(String(b));
    if (filter.operator === 'starts_with') return String(a).startsWith(String(b));
    if (filter.operator === 'ends_with') return String(a).endsWith(String(b));
    if (filter.operator === 'is_not') return a !== b;
    if (filter.operator === 'greater' || filter.operator === 'after') return a > b;
    if (filter.operator === 'greater_equal' || filter.operator === 'on_or_after') return a >= b;
    if (filter.operator === 'less' || filter.operator === 'before') return a < b;
    if (filter.operator === 'less_equal' || filter.operator === 'on_or_before') return a <= b;
    return a === b;
  }
  function databaseForQuery(dbValue: unknown): MdDatabase | null {
    if (dbValue && typeof dbValue === 'object' && canonicalDatabases.has(dbValue as object)) return dbValue as MdDatabase;
    const candidate = dbValue as any;
    if (candidate && validId(candidate.id)) {
      const stored = mdDatabaseReadCanonical(candidate.id);
      if (stored && stored.revision === candidate.revision && stored.updated === candidate.updated) return stored;
    }
    return mdDatabaseSanitise(dbValue);
  }
  /* The rows a membership contributes, composed at view time. db.rows stays
     purely what the database document owns — so nothing a member contributes
     can be written back as a V3 row or offered to sync as one. What the member
     is CALLED comes from mdRecordResolve; what this database says ABOUT it
     comes from the entry's cells. A member whose record is gone simply is not
     there: the ref outlives the record by design (see md-resolve.ts), and a
     view is a render, not an audit. */
  function memberRowsFor(dbId: string): MdDatabaseRow[] {
    /* The Main DB's members are every record on the account, and composing them
       is md-librarian.ts's job — the same function the AI Organizer reads, so
       the table and the agent can never see two different accounts. */
    if (isLibrarian(dbId)) return g.mdLibrarianRows ? g.mdLibrarianRows() : [];
    /* Omana People's members are the roster the server last handed over, and
       composing them is app/md-omana.ts's job — the same function the surface's
       re-share panel reads, so the gallery and the panel can never disagree
       about who was invited. */
    if (isOmana(dbId)) return g.mdOmanaRows ? g.mdOmanaRows() : [];
    if (!g.mdMembershipList || !g.mdRecordResolve) return [];
    /* The members are asked for FIRST, and a database nobody has filed into
       returns here having opened nothing. mdRecordHomes walks the whole of
       storage three times over — two day maps and the project index — and the
       Databases sheet counts every database on the account, so building the
       homes before this question cost nine wasted walks per open on an account
       with ten databases: 112 ms to compose nothing, ten times over. */
    const entries = g.mdMembershipList(dbId);
    if (!entries.length) return [];
    /* The homes are read from storage ONCE per call and handed to every
       resolve — a bare resolve would rebuild the day maps per member. */
    const homes = g.mdRecordHomes ? g.mdRecordHomes() : undefined;
    const out: MdDatabaseRow[] = [];
    for (const entry of entries) {
      const resolved = g.mdRecordResolve(entry.k, homes);
      if (!resolved) continue;
      /* A member inside its thirty-day buffer is skipped here, exactly as a
         member that is gone is (0.3) — so nothing a person deleted reappears in
         a table before the surface that can say WHY exists. Phase 4.4 replaces
         this `continue` with the countdown row. */
      if (resolved.trashed) continue;
      out.push({ id: entry.id, values: { ...entry.v, title: resolved.title },
        body: resolved.text || '', created: entry.at, updated: entry.at, member: entry.k });
    }
    return out;
  }
  /* ── the view's rows, built once per change ──────────────────────────────
     Filtering, decorating and sorting every row on the database. Opening the
     Main DB on a 5,000-record account ran this TWENTY-ONE times — 794 ms of
     one 1,815 ms blocking task — to draw sixty-two virtualised rows. The
     surface already wraps its call in useMemo, but the key is the database
     object, and a render that rebuilds that object rebuilds the sort with it.

     The result is a pure function of (this database, this view, storage), so
     that is exactly the key. `revision` and `updated` are in it because a
     caller may hand this a database it is holding mid-edit rather than the
     stored one — databaseForQuery accepts that — and answering such a caller
     from the stored version's cache would show them somebody else's rows.
     mdStorageRevision() covers what the database document cannot see at all:
     member rows composed from records that live outside it. */
  interface MdViewRows { filtered: MdDatabaseRow[]; sorted: MdDatabaseRow[] | null }
  const viewRowsMemo = new Map<string, MdViewRows>();
  let viewRowsAt: number | null = null;

  /** The identity of one answer: this database at this version, this view, and
      a device nothing has been written to since. `null` when there is no
      revision counter to lean on, which reads fresh every time as before. */
  function viewRowsKey(db: MdDatabase, viewId: string): string | null {
    if (typeof g.mdStorageRevision !== 'function') return null;
    return `${db.id} ${viewId} ${db.revision ?? ''} ${db.updated ?? ''}`;
  }

  function viewRowsFor(db: MdDatabase, view: MdDatabaseView): MdViewRows {
    const memoKey = viewRowsKey(db, view.id);
    if (memoKey === null) return { filtered: filteredRowsForView(db, view), sorted: null };
    const revision = g.mdStorageRevision();
    if (viewRowsAt !== revision) { viewRowsMemo.clear(); viewRowsAt = revision; }
    let held = viewRowsMemo.get(memoKey);
    if (!held) { held = { filtered: filteredRowsForView(db, view), sorted: null }; viewRowsMemo.set(memoKey, held); }
    return held;
  }

  function viewOf(db: MdDatabase, viewId: unknown): MdDatabaseView | null {
    return db.views.find((v) => v.id === viewId)
      || db.views.find((v) => v.id === db.activeViewId) || db.views[0] || null;
  }

  function mdDatabaseRowsForView(dbValue: unknown, viewId?: unknown): MdDatabaseRow[] {
    const db = databaseForQuery(dbValue); if (!db) return [];
    const view = viewOf(db, viewId); if (!view) return [];
    const held = viewRowsFor(db, view);
    if (!held.sorted) held.sorted = sortRowsForView(db, view, held.filtered);
    /* A copy on the way out: the surface hands this straight to React, and a
       caller that sorted or spliced it in place would be editing what every
       later reader sees. The rows inside are already clones. */
    return held.sorted.slice();
  }

  /** How many rows this view holds. Filters change a count; sorting cannot — so
      this stops at the filter rather than paying for an order nobody reads, and
      it reads the same filtered set the sorted rows are built from. The
      Databases sheet asks this of every database on every render, to write
      "5000 records" under its name, and used to get it by sorting all five
      thousand — which for the Main DB meant composing the whole account. */
  function mdDatabaseRowCount(dbValue: unknown, viewId?: unknown): number {
    const db = databaseForQuery(dbValue); if (!db) return 0;
    const view = viewOf(db, viewId); if (!view) return 0;
    return viewRowsFor(db, view).filtered.length;
  }

  function filteredRowsForView(db: MdDatabase, view: MdDatabaseView): MdDatabaseRow[] {
    const everyRow = [...db.rows, ...memberRowsFor(db.id)];
    return everyRow.filter((row) => view.filters.every((filter) => passes(mdDatabaseValue(db, row, filter.propertyId), filter)));
  }

  function sortRowsForView(db: MdDatabase, view: MdDatabaseView, out: MdDatabaseRow[]): MdDatabaseRow[] {
    const sortProps = view.sorts.map((sort) => db.properties.find((prop) => prop.id === sort.propertyId));
    const decorated = out.map((row) => ({ row, keys: view.sorts.map((sort) => mdDatabaseValue(db, row, sort.propertyId)) }));
    decorated.sort((a, b) => { for (let index = 0; index < view.sorts.length; index++) {
      const sort = view.sorts[index]!, prop = sortProps[index]; if (!prop) continue;
      const empty = isEmpty(a.keys[index]) || isEmpty(b.keys[index]);
      const compared = compareForSort(a.keys[index], b.keys[index], prop);
      if (compared) return empty ? compared : sort.direction === 'desc' ? -compared : compared;
    } return a.row.created - b.row.created; });
    return decorated.map((item) => item.row);
  }
  function mdDatabaseGroups(dbValue: unknown, viewId?: unknown): { id: string; label: string; rows: MdDatabaseRow[] }[] {
    const db = databaseForQuery(dbValue); if (!db) return [];
    const view = db.views.find((v) => v.id === viewId) || db.views[0], prop = db.properties.find((p) => p.id === view?.groupBy);
    const source = mdDatabaseRowsForView(db, view?.id); if (!view || !prop) return [{ id: 'all', label: 'All', rows: source }];
    const map = new Map<string, MdDatabaseRow[]>();
    // Every defined option is a group even with no rows — a board column you
    // cannot drop onto because it is hidden is a broken promise.
    for (const option of prop.options || []) map.set(option.id, []);
    for (const row of source) { const raw = mdDatabaseValue(db, row, prop.id), keys = Array.isArray(raw) ? raw.length ? raw.map(String) : [''] : [String(raw ?? '')]; for (const key of keys) map.set(key, [...(map.get(key) || []), row]); }
    const optionName = (key: string) => prop.options?.find((o) => o.id === key)?.name || key || 'Empty';
    const groups = [...map].map(([key, groupRows]) => ({ id: key || 'empty', label: optionName(key), rows: groupRows }));
    return groups.sort((a, b) => {
      if (a.id === 'empty' || b.id === 'empty') return a.id === b.id ? 0 : a.id === 'empty' ? 1 : -1;
      const optionRank = (key: string) => prop.options?.findIndex((option) => option.id === key) ?? -1;
      const ranked = optionRank(a.id) - optionRank(b.id);
      const compared = prop.options && ranked ? ranked : a.label.localeCompare(b.label);
      return (view.groupDirection === 'desc' ? -1 : 1) * compared;
    });
  }

  function mdDatabaseDelete(dbId: unknown): boolean {
    if (!validId(dbId) || isShipped(dbId)) return false;
    const prior = mdDatabaseReadCanonical(dbId);
    let ok = g.mdRemove(keyFor(dbId)); const prefix = rowPrefixFor(dbId);
    for (const key of g.mdStorageKeys(prefix)) ok = g.mdRemove(key) && ok;
    if (prior) for (const view of prior.views) ok = g.mdRemove(viewKeyFor(view.id)) && ok;
    /* A form is a contract to write into this library. With the library gone
       the contract cannot be honoured, so it goes with it rather than sitting
       there offering somebody a submission that can only fail. */
    for (const form of mdFormList(String(dbId))) { const gone = g.mdRemove(formKeyFor(form.id)); if (gone) notifyForm(form.id, null); ok = gone && ok; }
    if (ok) {
      canonicalByDatabase.delete(String(dbId)); historyByDatabase.delete(String(dbId));
      /* Exactly the argument made for forms one block up. A relation is also a
         contract against this library, and it cannot be honoured either: its
         ids can never resolve again and a rollup reading through it is reading
         a database that is gone. So the configuration and the dead ids go, and
         the column stays behind unconfigured — which is a state the property
         menu already knows how to show and the person can re-point.

         change() is reached only for a database that actually holds such a
         relation, and never for the Main DB: it is virtual, and writing it
         here would persist the one document that must stay materialised. */
      const dead = String(dbId);
      for (const other of mdDatabaseList()) {
        if (other.id === dead || isShipped(other.id)) continue;
        if (!other.properties.some((p) => p.type === 'relation' && p.relation?.databaseId === dead)) continue;
        change(other.id, (db) => {
          const orphaned = new Set<string>();
          for (const prop of db.properties) {
            if (prop.type !== 'relation' || prop.relation?.databaseId !== dead) continue;
            orphaned.add(prop.id); prop.relation = undefined;
            for (const row of db.rows) row.values[prop.id] = [];
          }
          for (const prop of db.properties) {
            if (prop.type === 'rollup' && prop.rollup && orphaned.has(prop.rollup.relationPropertyId)) prop.rollup = undefined;
          }
        });
      }
      notify(dbId, null); notifyHistory(String(dbId));
    } return ok;
  }
  function mdDatabaseLock(dbId: unknown, locked: unknown): MdDatabase | null {
    return change(dbId, (db) => { db.locked = !!locked; });
  }
  function mdDatabaseTemplateAdd(dbId: unknown, name: unknown = 'New template'): MdDatabase | null {
    return change(dbId, (db) => { db.templates = db.templates || []; if (db.templates.length >= 50) return;
      db.templates.push({ id: id('t'), name: text(name, 120) || 'New template', body: '', values: {} });
    });
  }
  function mdDatabaseTemplateUpdate(dbId: unknown, templateId: unknown, patch: unknown): MdDatabase | null {
    return change(dbId, (db) => { const template = db.templates?.find((item) => item.id === templateId);
      if (!template || !patch || typeof patch !== 'object') return; const p = patch as any;
      if (typeof p.name === 'string' && p.name.trim()) template.name = text(p.name, 120);
      if (typeof p.body === 'string') template.body = p.body.slice(0, 500000);
      if (p.values && typeof p.values === 'object' && !Array.isArray(p.values)) template.values = clone(p.values);
      if ('icon' in p) template.icon = text(p.icon, 80) || undefined;
    });
  }
  function mdDatabaseTemplateRemove(dbId: unknown, templateId: unknown): MdDatabase | null {
    return change(dbId, (db) => { db.templates = (db.templates || []).filter((item) => item.id !== templateId); });
  }
  function mdDatabaseRowAddFromTemplate(dbId: unknown, templateId: unknown): MdDatabase | null {
    const db = mdDatabaseRead(dbId), template = db?.templates?.find((item) => item.id === templateId); if (!db || !template) return db;
    const saved = mdDatabaseRowAdd(db.id, template.values), newest = saved?.rows[saved.rows.length - 1];
    return newest && template.body ? mdDatabaseRowBodySet(db.id, newest.id, template.body) : saved;
  }
  function mdDatabaseAutomationAdd(dbId: unknown): MdDatabase | null {
    return change(dbId, (db) => { db.automations = db.automations || []; if (db.automations.length >= 50) return;
      db.automations.push({ id: id('a'), name: 'New automation', enabled: true, trigger: 'row_added', action: 'set_property' });
    });
  }
  function mdDatabaseAutomationUpdate(dbId: unknown, automationId: unknown, patch: unknown): MdDatabase | null {
    return change(dbId, (db) => { const automation = db.automations?.find((item) => item.id === automationId);
      if (!automation || !patch || typeof patch !== 'object') return; const p = patch as any;
      if (typeof p.name === 'string' && p.name.trim()) automation.name = text(p.name, 120);
      if (typeof p.enabled === 'boolean') automation.enabled = p.enabled;
      if (['row_added', 'property_changed'].includes(p.trigger)) automation.trigger = p.trigger;
      if (typeof p.triggerPropertyId === 'string') automation.triggerPropertyId = p.triggerPropertyId;
      if (['set_property', 'add_row'].includes(p.action)) automation.action = p.action;
      if (typeof p.actionPropertyId === 'string') automation.actionPropertyId = p.actionPropertyId;
      if ('value' in p) automation.value = clone(p.value);
    });
  }
  function mdDatabaseAutomationRemove(dbId: unknown, automationId: unknown): MdDatabase | null {
    return change(dbId, (db) => { db.automations = (db.automations || []).filter((item) => item.id !== automationId); });
  }
  /* ── Phase 1C-2 · forms ──────────────────────────────────────────────────
     A form is an input contract, not a permission. It declares the fields it
     accepts, the library they land in, and exactly one command. Submission
     projects the caller's payload onto the DECLARED fields and nothing else,
     so a key nobody declared reaches no cell — that is what makes holding a
     form strictly weaker than holding a write to its destination.

     Forms are discovered by scanning storage keys, exactly like databases.
     The database document therefore carries no form list at all, and CAS
     isolation between authoring a form and editing a schema holds by
     construction rather than by care. */
  const formKeyFor = (formId: string): string => `md:form:${formId}`;
  const FORM_KEY_RE = /^form:([a-z0-9]{6,40})$/;
  const FORM_COMMANDS = ['database.row.add'];
  const MAX_FORM_FIELDS = MAX_PROPERTIES;

  /* Structural sanitise of an independent form document. Property references
     are validated as strings only — the parent schema is deliberately not
     consulted here (the document must be acceptable before its library
     arrives, mirroring row and view documents); mdFormSubmit re-checks every
     reference against the live schema at the moment it writes. */
  function mdFormDocSanitise(value: unknown, expectedFormId?: unknown): MdFormDoc | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as any;
    const fid = validId(raw.id) ? raw.id : validId(expectedFormId) ? expectedFormId : null;
    if (!fid || (validId(expectedFormId) && fid !== expectedFormId)) return null;
    if (!validId(raw.databaseId)) return null;
    /* An unrecognised command is refused outright rather than defaulted. A
       form naming a command this build cannot read must not quietly fall back
       to one it can — that would run something nobody authored. */
    if (!FORM_COMMANDS.includes(raw.command)) return null;
    const fields: MdFormField[] = [], seen = new Set<string>();
    for (const item of (Array.isArray(raw.fields) ? raw.fields : []).slice(0, MAX_FORM_FIELDS)) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const f = item as any;
      const propertyId = typeof f.propertyId === 'string' && f.propertyId.length > 0 && f.propertyId.length <= 80 ? f.propertyId : '';
      if (!propertyId || seen.has(propertyId)) continue;
      seen.add(propertyId);
      const field: MdFormField = { propertyId };
      const label = text(f.label, 120); if (label) field.label = label;
      const help = text(f.help, 400); if (help) field.help = help;
      if (f.required === true) field.required = true;
      if ('default' in f && f.default !== undefined) {
        /* A default that will not survive storage is dropped rather than
           carried, so a form never promises a prefill it cannot deliver. */
        try { const kept = clone(f.default); if (JSON.stringify(kept ?? null).length <= 4000) field.default = kept; }
        catch { /* not storable — the field simply has no default */ }
      }
      fields.push(field);
    }
    /* A form with no fields accepts nothing; it is not an input contract. */
    if (!fields.length) return null;
    return { version: 1, id: fid, databaseId: String(raw.databaseId), name: text(raw.name, 120) || 'Untitled form',
      intro: text(raw.intro, 2000) || undefined, fields, command: 'database.row.add',
      updated: finite(raw.updated, Date.now()), revision: Math.max(1, finite(raw.revision, 1)) };
  }
  function formDocFromStorage(formId: string): MdFormDoc | null {
    return mdFormDocSanitise(g.mdRead(formKeyFor(formId), null), formId);
  }
  function notifyForm(formId: string, value: MdFormDoc | null): void {
    if (typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') {
      g.dispatchEvent(new g.CustomEvent('mdformchange', { detail: { id: formId, value: value ? clone(value) : null } }));
    }
    if (typeof g.mdSyncSoon === 'function') g.mdSyncSoon();
  }
  function mdFormRead(formId: unknown): MdFormDoc | null {
    return validId(formId) ? formDocFromStorage(formId) : null;
  }
  function mdFormList(databaseId?: unknown): MdFormDoc[] {
    const out: MdFormDoc[] = [];
    for (const key of g.mdStorageKeys('md:form:')) {
      const formId = key.slice('md:form:'.length);
      if (!validId(formId)) continue;
      const doc = formDocFromStorage(formId);
      if (!doc || (databaseId != null && doc.databaseId !== databaseId)) continue;
      out.push(doc);
    }
    return out.sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  }
  function mdFormSave(value: unknown): MdFormDoc | null {
    const incoming = mdFormDocSanitise(value);
    if (!incoming) return null;
    const prior = formDocFromStorage(incoming.id);
    const comparable = (doc: MdFormDoc): string => JSON.stringify({ ...doc, updated: 0, revision: 0 });
    /* A save that changes nothing does not revise the document — an idle
       re-save must not hand another device a CAS conflict to resolve. */
    if (prior && comparable(prior) === comparable(incoming)) return prior;
    const next: MdFormDoc = { ...incoming, updated: Date.now(),
      revision: prior ? prior.revision + 1 : Math.max(1, incoming.revision) };
    if (!g.mdWrite(formKeyFor(next.id), next)) return null;
    notifyForm(next.id, next);
    return next;
  }
  function mdFormCreate(databaseId: unknown, seed?: unknown): MdFormDoc | null {
    if (!validId(databaseId)) return null;
    const db = mdDatabaseRead(databaseId);
    if (!db) return null;
    const raw = seed && typeof seed === 'object' && !Array.isArray(seed) ? seed as any : {};
    /* The default field set is everything a person can actually fill in.
       Computed properties are never offered, because nothing can be written
       to them and a field that cannot accept an answer is a lie. */
    const fields = Array.isArray(raw.fields) && raw.fields.length ? raw.fields
      : db.properties.filter((prop) => !COMPUTED.has(prop.type)).map((prop) => ({ propertyId: prop.id, label: prop.name }));
    return mdFormSave({ version: 1, id: validId(raw.id) ? raw.id : id('fm'), databaseId,
      name: text(raw.name, 120) || `${db.name} form`, intro: raw.intro, fields,
      command: 'database.row.add', updated: Date.now(), revision: 1 });
  }
  /* databaseId and command are deliberately not patchable: repointing a form
     at another library, or at another command, is a different contract and
     has to be authored as one. */
  function mdFormUpdate(formId: unknown, patch: unknown): MdFormDoc | null {
    const prior = mdFormRead(formId);
    if (!prior || !patch || typeof patch !== 'object' || Array.isArray(patch)) return prior;
    const p = patch as any, next: any = { ...prior };
    if ('name' in p) next.name = p.name;
    if ('intro' in p) next.intro = p.intro;
    if ('fields' in p) next.fields = p.fields;
    return mdFormSave(next) || prior;
  }
  function mdFormRemove(formId: unknown): boolean {
    if (!validId(formId) || !formDocFromStorage(formId)) return false;
    const ok = g.mdRemove(formKeyFor(formId));
    if (ok) notifyForm(formId, null);
    return ok;
  }

  /* One submission: project, validate, then run exactly one command. Every
     refusal happens BEFORE anything is written, so a rejected submission
     leaves no partial row behind. */
  function mdFormSubmit(formId: unknown, input?: unknown): MdFormSubmitResult {
    const form = mdFormRead(formId);
    if (!form) return { ok: false, reason: 'missing_form', errors: [] };
    const db = mdDatabaseRead(form.databaseId);
    if (!db) return { ok: false, reason: 'missing_database', errors: [] };
    if (db.locked) return { ok: false, reason: 'locked', errors: [] };
    const byId = new Map(db.properties.map((prop) => [prop.id, prop]));
    const supplied = input && typeof input === 'object' && !Array.isArray(input) ? input as Record<string, unknown> : {};
    const errors: MdFormFieldError[] = [], projected: Record<string, unknown> = {};
    for (const field of form.fields) {
      const prop = byId.get(field.propertyId);
      /* Declarations are checked against the LIVE schema at submission, not
         at authoring time: a property removed since the form was written must
         fail loudly here, never land somewhere else. */
      if (!prop) { errors.push({ propertyId: field.propertyId, reason: 'unknown_field' }); continue; }
      if (COMPUTED.has(prop.type)) { errors.push({ propertyId: field.propertyId, reason: 'not_writable' }); continue; }
      const given = Object.prototype.hasOwnProperty.call(supplied, field.propertyId) ? supplied[field.propertyId] : undefined;
      const raw = given === undefined && 'default' in field ? field.default : given;
      const value = cleanValue(prop, raw);
      /* cleanValue answers "empty" for a value it could not read — a bad date,
         an option id from another library. Writing that as a blank cell would
         turn a typo into a silently wrong record, so the submission is refused
         instead of quietly downgraded. */
      if (!isEmpty(raw) && isEmpty(value)) { errors.push({ propertyId: field.propertyId, reason: 'invalid' }); continue; }
      /* Required means answered. For a checkbox that is `true`, not "present":
         an unticked box is a complete answer everywhere else, and here it is
         the one the form asked for. */
      if (field.required && (prop.type === 'checkbox' ? value !== true : isEmpty(value))) {
        errors.push({ propertyId: field.propertyId, reason: 'required' }); continue;
      }
      projected[field.propertyId] = value;
    }
    if (errors.length) return { ok: false, reason: 'invalid', errors };
    /* Nothing outside form.fields was read from the payload above. An
       undeclared key reaches no cell, whatever it was named. */
    const before = new Set(db.rows.map((row) => row.id));
    const saved = mdDatabaseRowAdd(db.id, projected);
    /* The submission's row is pushed before any row_added automation runs, so
       the first id that was not there before is this submission's. */
    const added = saved ? saved.rows.find((row) => !before.has(row.id)) : null;
    if (!saved || !added) return { ok: false, reason: 'storage_full', errors: [] };
    return { ok: true, formId: form.id, databaseId: db.id, rowId: added.id, values: clone(projected) };
  }

  const mdDatabaseLine = (dbId: unknown): string => validId(dbId) ? `![Database](mddb:${dbId})` : '';
  function mdDatabaseIdFromLine(line: unknown): string { const match = typeof line === 'string' ? LINE_RE.exec(line.trim()) : null; return match ? match[1] || '' : ''; }
  function mdDatabaseBlockOf(line: unknown): { key: string; height: number } | null { const dbId = mdDatabaseIdFromLine(line); return dbId ? { key: `database:${dbId}`, height: 390 } : null; }
  /* TRASH OUTRANKS THE WIRE. While a database's Trash entry is live, none of
     its documents are offered and no pulled copy of one — metadata, row, row
     state, view, form — may land: applying a stale peer's copy is how a
     deleted database came back forty-two seconds after it left (staging,
     2026-09-01), each side re-pushing the library at a newer revision than
     the deletion. A null still lands (a removal never resurrects anything),
     and the Trash door's own hand is let through — that is what restore is.
     Withholding the offer is also what lets the door's tombstone nulls stand:
     the server's copy empties and stays empty. */
  const inTrash = (dbId: unknown): boolean =>
    typeof dbId === 'string'
    && !(typeof g.mdTrashBusy === 'function' && g.mdTrashBusy())
    && typeof g.mdTrashRead === 'function'
    && !!g.mdTrashRead(`database:${dbId}`);
  function mdDatabaseSyncDocs(): Record<string, unknown> {
    const out: Record<string, unknown> = {}; for (const key of g.mdStorageKeys(DB_PREFIX)) { const dbId = key.slice(DB_PREFIX.length); if (!validId(dbId) || inTrash(dbId)) continue; const db = mdDatabaseRead(dbId); if (!db) continue; out[`database:${dbId}`] = metadataOf(db); for (const row of db.rows) out[`database-row:${dbId}:${row.id}`] = row; for (const view of db.views) out[`view:${view.id}`] = viewDocFromStorage(view.id) || docFromView(view, dbId, 1, db.updated); }
    /* Forms ride alongside, discovered from storage rather than from any
       database's document — a form for a library this device does not hold
       is still the person's, and still syncs. */
    for (const form of mdFormList()) { if (inTrash(form.databaseId)) continue; out[`form:${form.id}`] = form; }
    /* THE STATE TRAVELS TOO, or it is a private note rather than a merge.
       Enumerated from its own key space: a row's state and a row's document are
       written and removed together, but reading them from one place keeps this
       honest if that ever stops being true. */
    if (crdtOn()) {
      for (const key of g.mdStorageKeys(ROW_CRDT_PREFIX)) {
        const syncK = `crdt:database-row:${key.slice(ROW_CRDT_PREFIX.length)}`;
        const crdtMatch = ROW_CRDT_RE.exec(syncK);
        if (!crdtMatch || inTrash(crdtMatch[1])) continue;
        const state = g.mdRead(key, null);
        if (typeof state !== 'string' || !state) continue;
        out[syncK] = state;
      }
    }
    return out;
  }

  /* A state arriving from the other device.

     THIS IS THE ONE PLACE THE CRDT OVERRULES THE CLOCK, and it does it in one
     direction only — state wins, and the readable row is rewritten from it.
     Anything less is two answers to one question.

     With no state of our own — every row written before E5 is in this position
     — ADOPT theirs and lay this device's row on top, rather than seeding a
     rival and merging the two. Two states seeded independently share no
     history, so a merge would keep one of them whole. Adopting is what gives
     the two devices a common ancestor from this moment on.

     It does not rescue the meeting itself: where the two disagree right now
     there is no shared past to consult. The first meeting is exactly as good as
     today, and every one after it is better. */
  function applyRowCrdt(dbId: string, rowId: string, value: unknown): boolean {
    if (value == null) return g.mdRemove(rowCrdtKeyFor(dbId, rowId));
    if (!crdtOn() || typeof g.mdCrdtMerge !== 'function') return false;
    const held = g.mdRead(rowCrdtKeyFor(dbId, rowId), null);
    const local = mdDatabaseRowSanitise(g.mdRead(rowKeyFor(dbId, rowId), null), rowId);
    const merged = held ? g.mdCrdtMerge(held, value)
      : (local ? g.mdCrdtApplyFields(value, local) : value);
    if (!merged) return false;
    const fields = g.mdCrdtFields(merged);
    const row = fields ? mdDatabaseRowSanitise(fields, rowId) : null;
    if (!row) return false;                      // unreadable: today's answer stands
    g.mdWrite(rowCrdtKeyFor(dbId, rowId), merged);
    const ok = g.mdWrite(rowKeyFor(dbId, rowId), row);
    if (ok) { canonicalByDatabase.delete(dbId); notify(dbId, mdDatabaseReadCanonical(dbId)); }
    return ok;
  }
  function mdDatabaseApplyDoc(key: unknown, value: unknown): boolean {
    if (typeof key !== 'string') return false;
    /* Before the row match below, which is anchored at `database-row:` and
       would not see this — but reading it first says what it is. */
    const stateMatch = ROW_CRDT_RE.exec(key);
    if (stateMatch) { if (value != null && inTrash(stateMatch[1])) return false; return applyRowCrdt(stateMatch[1]!, stateMatch[2]!, value); }
    const rowMatch = /^database-row:([a-z0-9]{6,40}):([a-z0-9]{6,40})$/.exec(key);
    if (rowMatch) { const dbId = rowMatch[1]!, rowId = rowMatch[2]!; if (value == null) { const ok = g.mdRemove(rowKeyFor(dbId, rowId)); if (ok) { rowCrdtFollow(dbId, rowId, null, false); canonicalByDatabase.delete(dbId); notify(dbId, mdDatabaseReadCanonical(dbId)); } return ok; } if (inTrash(dbId)) return false; const row = mdDatabaseRowSanitise(value, rowId); if (!row) return false; const ok = g.mdWrite(rowKeyFor(dbId, rowId), row); if (ok) { rowCrdtFollow(dbId, rowId, row, false); canonicalByDatabase.delete(dbId); notify(dbId, mdDatabaseReadCanonical(dbId)); } return ok; }
    const viewMatch = VIEW_KEY_RE.exec(key);
    if (viewMatch) { const viewId = viewMatch[1]!; const priorDoc = viewDocFromStorage(viewId);
      if (value == null) { const ok = g.mdRemove(viewKeyFor(viewId)); if (ok && priorDoc) { canonicalByDatabase.delete(priorDoc.databaseId); notify(priorDoc.databaseId, mdDatabaseReadCanonical(priorDoc.databaseId)); } return ok; }
      const doc = mdViewDocSanitise(value, viewId); if (!doc || inTrash(doc.databaseId)) return false; const ok = g.mdWrite(viewKeyFor(viewId), doc); if (ok) { canonicalByDatabase.delete(doc.databaseId); notify(doc.databaseId, mdDatabaseReadCanonical(doc.databaseId)); } return ok; }
    /* A form document changes no composed database, so no cache is dropped
       and no mddatabasechange is fired — only the form's own listeners. */
    const formMatch = FORM_KEY_RE.exec(key);
    if (formMatch) { const formId = formMatch[1]!;
      if (value == null) { const ok = g.mdRemove(formKeyFor(formId)); if (ok) notifyForm(formId, null); return ok; }
      const doc = mdFormDocSanitise(value, formId); if (!doc || inTrash(doc.databaseId)) return false;
      const ok = g.mdWrite(formKeyFor(formId), doc); if (ok) notifyForm(formId, doc); return ok; }
    if (!key.startsWith('database:')) return false; const dbId = key.slice('database:'.length); if (!validId(dbId)) return false;
    if (value == null) { const ok = g.mdRemove(keyFor(dbId)); if (ok) { canonicalByDatabase.delete(dbId); notify(dbId, null); } return ok; }
    if (inTrash(dbId)) return false;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const prepared = prepareViews(value, dbId); if (!prepared) return false;
    const clean = mdDatabaseSanitise(prepared.working, dbId); if (!clean) return false; let ok = g.mdWrite(keyFor(dbId), metadataOf(clean, prepared.contractedIds));
    for (const row of clean.rows) {
      const wrote = g.mdWrite(rowKeyFor(dbId, row.id), row);
      if (wrote) rowCrdtFollow(dbId, row.id, row, false);   // theirs: follow, never seed
      ok = wrote && ok;
    } if (ok) { canonicalByDatabase.delete(dbId); notify(dbId, mdDatabaseReadCanonical(dbId)); } return ok;
  }
  function mdDatabaseButtonRun(dbId: unknown, rowId: unknown, propertyId: unknown): MdDatabase | null {
    const db = mdDatabaseRead(dbId); if (!db) return null; const prop = db.properties.find((p) => p.id === propertyId), button = prop?.button;
    if (!button) return db; if (button.action === 'add_row') return mdDatabaseRowAdd(db.id);
    if (button.action === 'set_value' && button.targetPropertyId) return mdDatabaseCellSet(db.id, rowId, button.targetPropertyId, button.value);
    return db;
  }

  /* A deterministic, full-surface database used by the local `?qa=db500`
     laboratory. Keeping this behind an explicit QA URL makes the stress case
     reproducible without adding demo furniture to the everyday interface. */
  function mdDatabaseDemoCreate(rowCount: unknown = 500): { databaseId: string; relationDatabaseId: string; rows: number; line: string } | null {
    const count = Math.max(1, Math.min(500, Math.floor(finite(rowCount, 500))));
    const databaseId = 'd500laboratory', relationDatabaseId = 'd500organizations';
    const now = Date.now(), day = 86400000;
    const option = (id: string, name: string, color: string): MdDatabaseOption => ({ id, name, color });
    const orgProperties: MdDatabaseProperty[] = [
      { id: 'title', name: 'Organization', type: 'title' },
      { id: 'arrvalue', name: 'Annual value', type: 'number', format: 'currency:USD' },
      { id: 'sectorprop', name: 'Sector', type: 'select', options: [
        option('sectorhealth', 'Health', 'mint'), option('sectoredu', 'Education', 'blue'),
        option('sectorfin', 'Finance', 'amber'), option('sectormedia', 'Media', 'rose'),
        option('sectorpublic', 'Public good', 'cyan') ] },
      { id: 'headquarter', name: 'Headquarters', type: 'place' },
      { id: 'workitems', name: 'Work items', type: 'relation', relation: { databaseId, limit: 'many', reciprocalPropertyId: 'accountprop' } },
    ];
    const orgRows: MdDatabaseRow[] = Array.from({ length: 24 }, (_, index) => {
      const n = index + 1, created = now - (180 - n * 3) * day;
      const cities = ['Cairo, Egypt', 'Alexandria, Egypt', 'Dubai, UAE', 'Riyadh, Saudi Arabia', 'Amman, Jordan', 'Doha, Qatar'];
      const names = ['Amanah Health', 'Northstar Learning', 'Nile Works', 'Mizan Finance', 'Safa Media', 'Wasl Foundation'];
      return { id: `org${String(n).padStart(6, '0')}`, values: {
        title: `${names[index % names.length]} ${Math.floor(index / names.length) + 1}`,
        arrvalue: 48000 + index * 13750, sectorprop: ['sectorhealth', 'sectoredu', 'sectorfin', 'sectormedia', 'sectorpublic'][index % 5],
        headquarter: cities[index % cities.length], workitems: [],
      }, body: `# Partnership brief\n\nRelationship owner: ${['Mariam', 'Yusuf', 'Leila', 'Omar'][index % 4]}\n\n- Strategic account\n- Quarterly review enabled`,
        icon: ['✦', '◈', '◎', '◇'][index % 4], favorite: index < 4, comments: [], created, updated: created + 14 * day,
        createdBy: ['Mariam Hassan', 'Yusuf Ali'][index % 2], updatedBy: ['Leila Farouk', 'Omar Nasser'][index % 2] };
    });
    const orgView: MdDatabaseView = { id: 'vieworgs', name: 'Organizations', type: 'table',
      visiblePropertyIds: orgProperties.map((prop) => prop.id), sorts: [{ propertyId: 'arrvalue', direction: 'desc' }], filters: [],
      groupBy: 'sectorprop', groupDirection: 'asc', hiddenGroups: [], calculations: { arrvalue: 'sum', title: 'count' },
      datePropertyId: null, endDatePropertyId: null, chartPropertyId: 'arrvalue', chartType: 'bar',
      chartAggregation: 'sum',
      propertyWidths: { title: 240, arrvalue: 150, sectorprop: 140, headquarter: 190, workitems: 220 }, frozenPropertyIds: ['title'],
      wrapPropertyIds: ['workitems'], showDataSourceTitle: true, showVerticalLines: true, showPageIcon: true,
      wrapContent: true, openPagesIn: 'side_peek', conditionalColors: [] };
    const orgDb = mdDatabaseSave({ version: VERSION, id: relationDatabaseId, name: 'Demo Organizations', properties: orgProperties,
      rows: orgRows, views: [orgView], activeViewId: orgView.id, templates: [], automations: [], locked: false,
      created: now - 200 * day, updated: now, revision: 1 });
    if (!orgDb) return null;

    const priorityOptions = [option('prioritycritical', 'Critical', 'rose'), option('priorityhigh', 'High', 'amber'),
      option('prioritymedium', 'Medium', 'blue'), option('prioritylow', 'Low', 'mint')];
    const skillOptions = [option('skilldesign', 'Design', 'violet'), option('skillresearch', 'Research', 'cyan'),
      option('skillengineering', 'Engineering', 'blue'), option('skillstrategy', 'Strategy', 'amber'),
      option('skillwriting', 'Writing', 'rose'), option('skilloperations', 'Operations', 'mint')];
    const stageOptions = [option('stagenotstarted', 'Not started', 'blue'), option('stageprogress', 'In progress', 'amber'),
      option('stageblocked', 'Blocked', 'rose'), option('stagereview', 'In review', 'violet'), option('stagedone', 'Done', 'mint')];
    const properties: MdDatabaseProperty[] = [
      { id: 'title', name: 'Initiative', type: 'title' },
      { id: 'summaryprop', name: 'Summary', type: 'text' },
      { id: 'budgetprop', name: 'Budget', type: 'number', format: 'currency:USD' },
      { id: 'priorityprop', name: 'Priority', type: 'select', options: priorityOptions },
      { id: 'skillsprop', name: 'Capabilities', type: 'multi', options: skillOptions },
      { id: 'stageprop', name: 'Stage', type: 'status', options: stageOptions },
      { id: 'launchprop', name: 'Launch', type: 'date' },
      { id: 'approvedprop', name: 'Approved', type: 'checkbox' },
      { id: 'websiteprop', name: 'Brief URL', type: 'url' },
      { id: 'emailprop', name: 'Owner email', type: 'email' },
      { id: 'phoneprop', name: 'Owner phone', type: 'phone' },
      { id: 'peopleprop', name: 'Collaborators', type: 'person' },
      { id: 'filesprop', name: 'Files & media', type: 'files' },
      { id: 'healthprop', name: 'Health score', type: 'formula', formula: 'round(prop("Budget") / 1000 + if(prop("Approved"), 20, 0), 1)' },
      { id: 'accountprop', name: 'Organization', type: 'relation', relation: { databaseId: relationDatabaseId, limit: 'one', reciprocalPropertyId: 'workitems' } },
      { id: 'rollupprop', name: 'Account value', type: 'rollup', rollup: { relationPropertyId: 'accountprop', targetPropertyId: 'arrvalue', calculation: 'sum' } },
      { id: 'buttonprop', name: 'Open brief', type: 'button', button: { label: 'Open', action: 'open_row' } },
      { id: 'identityprop', name: 'Record ID', type: 'id' },
      { id: 'placeprop', name: 'Location', type: 'place' },
      { id: 'createdtime', name: 'Created time', type: 'created_time' },
      { id: 'createdby', name: 'Created by', type: 'created_by' },
      { id: 'updatedtime', name: 'Last edited', type: 'updated_time' },
      { id: 'updatedby', name: 'Last edited by', type: 'updated_by' },
    ];
    const owners = ['Mariam Hassan', 'Yusuf Ali', 'Leila Farouk', 'Omar Nasser', 'Salma Amin', 'Adam Khalil', 'Nour Adel', 'Zaynab Musa'];
    const places = ['Cairo, Egypt', 'Alexandria, Egypt', 'Dubai, UAE', 'Riyadh, Saudi Arabia', 'Amman, Jordan', 'Doha, Qatar', 'Remote'];
    const nouns = ['Knowledge atlas', 'Member portal', 'Launch narrative', 'Research library', 'Trust ledger', 'Community pulse',
      'Stewardship fund', 'Learning pathway', 'Partner cockpit', 'Field guide', 'Operations rhythm', 'Craft gallery'];
    const rows: MdDatabaseRow[] = Array.from({ length: count }, (_, index) => {
      const n = index + 1, created = now - (420 - index % 360) * day, updated = created + (index % 28) * day;
      const orgId = `org${String(index % 24 + 1).padStart(6, '0')}`;
      const stage = stageOptions[index % stageOptions.length]!.id, priority = priorityOptions[(index * 3) % priorityOptions.length]!.id;
      const collaborators = [owners[index % owners.length]!, owners[(index + 3) % owners.length]!];
      const skills = [skillOptions[index % skillOptions.length]!.id, skillOptions[(index + 2) % skillOptions.length]!.id];
      const launch = new Date(now + (index - 180) * day).toISOString().slice(0, 10);
      return { id: `work${String(n).padStart(6, '0')}`, values: {
        title: `${n}. ${nouns[index % nouns.length]}`,
        summaryprop: `${['Shape', 'Validate', 'Design', 'Ship', 'Measure'][index % 5]} a calm, useful ${nouns[index % nouns.length]!.toLocaleLowerCase()} for cohort ${Math.floor(index / 25) + 1}.`,
        budgetprop: 2500 + (index * 1379) % 92500, priorityprop: priority, skillsprop: skills, stageprop: stage,
        launchprop: launch, approvedprop: index % 3 !== 0, websiteprop: `https://example.com/mandana/initiative-${String(n).padStart(3, '0')}`,
        emailprop: `${owners[index % owners.length]!.toLocaleLowerCase().replace(' ', '.')}@example.com`,
        phoneprop: `+20 10 ${String(10000000 + index * 7919).slice(-8)}`, peopleprop: collaborators,
        filesprop: index % 4 === 0 ? [{ id: `file${String(n).padStart(6, '0')}`, name: `Initiative ${n} brief.pdf`,
          url: `https://example.com/files/initiative-${n}.pdf`, mime: 'application/pdf', size: 120000 + index * 913 }] : [],
        healthprop: null, accountprop: [orgId], rollupprop: null, buttonprop: null, identityprop: null,
        placeprop: places[index % places.length], createdtime: null, createdby: null, updatedtime: null, updatedby: null,
      }, body: `# ${nouns[index % nouns.length]}\n\n## Intent\n${['Clarify the decision and make the next action obvious.', 'Turn field insight into a reusable operating asset.', 'Create a humane system that stays powerful under a calm surface.'][index % 3]}\n\n## Checklist\n- [${index % 2 ? 'x' : ' '}] Research reviewed\n- [${index % 3 ? 'x' : ' '}] Owner aligned\n- [ ] Release note written\n\nTags: #database-lab #cohort-${Math.floor(index / 25) + 1}`,
        icon: ['✦', '◈', '◎', '◇', '△'][index % 5], favorite: index < 8,
        comments: index % 7 === 0 ? [{ id: `comment${String(n).padStart(6, '0')}`, author: owners[(index + 1) % owners.length]!,
          body: ['Ready for leadership review.', 'Please tighten the success measure.', 'The latest prototype feels much calmer.'][index % 3]!, created: updated - day }] : [],
        created, updated, createdBy: owners[(index + 2) % owners.length], updatedBy: owners[(index + 5) % owners.length] };
    });
    for (const row of rows) {
      const orgId = (row.values.accountprop as string[])[0], org = orgRows.find((item) => item.id === orgId);
      if (org) org.values.workitems = [...(org.values.workitems as string[]), row.id];
    }
    mdDatabaseSave({ ...orgDb, rows: orgRows });

    const compact = ['title', 'stageprop', 'priorityprop', 'peopleprop', 'launchprop', 'budgetprop'];
    const viewBase = { visiblePropertyIds: compact, sorts: [] as MdDatabaseSort[], filters: [] as MdDatabaseFilter[], groupBy: null,
      groupDirection: 'asc' as const, hiddenGroups: [] as string[], calculations: {} as Record<string, MdDatabaseCalculation>,
      datePropertyId: 'launchprop', endDatePropertyId: null, chartPropertyId: 'budgetprop', chartType: 'bar' as const,
      chartAggregation: 'sum' as const,
      propertyWidths: { title: 260, summaryprop: 320, stageprop: 150, priorityprop: 140, peopleprop: 210, launchprop: 140, budgetprop: 150 },
      frozenPropertyIds: ['title'], wrapPropertyIds: ['summaryprop', 'peopleprop'], showDataSourceTitle: true,
      showVerticalLines: true, showPageIcon: true, wrapContent: true, openPagesIn: 'side_peek' as const,
      conditionalColors: [{ id: 'ruleblocked', propertyId: 'stageprop', operator: 'is' as const, value: 'stageblocked', color: 'rose' }] };
    const allProperties = properties.map((prop) => prop.id);
    const views: MdDatabaseView[] = [
      { ...viewBase, id: 'viewallwork', name: 'All work', type: 'table', visiblePropertyIds: allProperties,
        sorts: [{ propertyId: 'launchprop', direction: 'asc' }], calculations: { title: 'count', budgetprop: 'sum', approvedprop: 'percent_checked', launchprop: 'date_range' } },
      { ...viewBase, id: 'viewboard', name: 'Delivery board', type: 'board', groupBy: 'stageprop', sorts: [{ propertyId: 'priorityprop', direction: 'asc' }] },
      { ...viewBase, id: 'viewtimeline', name: 'Launch timeline', type: 'timeline', visiblePropertyIds: ['title', 'stageprop', 'launchprop', 'peopleprop'] },
      { ...viewBase, id: 'viewcalendar', name: 'Calendar', type: 'calendar', visiblePropertyIds: ['title', 'stageprop', 'priorityprop', 'launchprop'] },
      { ...viewBase, id: 'viewgallery', name: 'Brief gallery', type: 'gallery', visiblePropertyIds: ['title', 'summaryprop', 'stageprop', 'filesprop'] },
      { ...viewBase, id: 'viewlist', name: 'Leadership list', type: 'list', filters: [{ propertyId: 'approvedprop', operator: 'checked' }],
        sorts: [{ propertyId: 'budgetprop', direction: 'desc' }], visiblePropertyIds: ['title', 'priorityprop', 'budgetprop', 'peopleprop'] },
      { ...viewBase, id: 'viewchart', name: 'Budget by stage', type: 'chart', groupBy: 'stageprop', chartPropertyId: 'budgetprop', chartType: 'bar' },
      { ...viewBase, id: 'viewdashboard', name: 'Command center', type: 'dashboard', chartPropertyId: 'priorityprop' },
      { ...viewBase, id: 'viewfeed', name: 'Activity feed', type: 'feed', visiblePropertyIds: ['title', 'summaryprop', 'updatedtime', 'updatedby'] },
      { ...viewBase, id: 'viewmap', name: 'Field map', type: 'map', visiblePropertyIds: ['title', 'placeprop', 'stageprop', 'peopleprop'] },
      { ...viewBase, id: 'viewform', name: 'Intake form', type: 'form', visiblePropertyIds: ['title', 'summaryprop', 'priorityprop', 'skillsprop', 'launchprop', 'peopleprop', 'placeprop'] },
      { ...viewBase, id: 'viewschedule', name: 'Schedule', type: 'schedule', visiblePropertyIds: ['title', 'stageprop', 'priorityprop', 'launchprop'] },
    ];
    const templates: MdDatabaseTemplate[] = [
      { id: 'templateinitiative', name: 'New initiative', icon: '✦', body: '# Intent\n\n## Outcome\n\n## Decisions\n\n## Next actions\n- [ ]',
        values: { stageprop: 'stagenotstarted', priorityprop: 'prioritymedium', skillsprop: ['skillstrategy'] } },
      { id: 'templateincident', name: 'Incident review', icon: '◇', body: '# Incident\n\n## What happened\n\n## Root cause\n\n## Repair\n- [ ]',
        values: { stageprop: 'stageprogress', priorityprop: 'prioritycritical', skillsprop: ['skillengineering', 'skilloperations'] } },
      { id: 'templatedecision', name: 'Decision memo', icon: '◈', body: '# Decision\n\n## Context\n\n## Options\n\n## Decision\n\n## Revisit date',
        values: { stageprop: 'stagereview', priorityprop: 'priorityhigh', skillsprop: ['skillresearch', 'skillwriting'] } },
    ];
    const automations: MdDatabaseAutomation[] = [
      { id: 'automationnewrow', name: 'New rows start unapproved', enabled: true, trigger: 'row_added', action: 'set_property', actionPropertyId: 'approvedprop', value: false },
      { id: 'automationapproval', name: 'Approved work enters review', enabled: true, trigger: 'property_changed', triggerPropertyId: 'approvedprop', action: 'set_property', actionPropertyId: 'stageprop', value: 'stagereview' },
    ];
    const saved = mdDatabaseSave({ version: VERSION, id: databaseId, name: `Database Laboratory · ${count} rows`, properties, rows, views,
      activeViewId: 'viewallwork', locked: false, templates, automations, created: now - 430 * day, updated: now, revision: 1 });
    return saved ? { databaseId, relationDatabaseId, rows: saved.rows.length, line: mdDatabaseLine(databaseId) } : null;
  }

  /* A deliberately lean scale fixture. The 500-row laboratory exercises every
     property; this one isolates interaction and rendering at the current 5k
     product ceiling without filling local storage with artificial page prose. */
  function mdDatabaseScaleCreate(): { databaseId: string; rows: number; line: string } | null {
    const databaseId = 'd5kscalelaboratory', now = Date.now(), day = 86400000;
    const stages: MdDatabaseOption[] = [
      { id: 'queued', name: 'Queued', color: 'blue' },
      { id: 'active', name: 'Active', color: 'amber' },
      { id: 'review', name: 'Review', color: 'violet' },
      { id: 'complete', name: 'Done', color: 'mint' },
    ];
    const properties: MdDatabaseProperty[] = [
      { id: 'title', name: 'Work item', type: 'title' },
      { id: 'stageprop', name: 'Stage', type: 'status', options: stages },
      { id: 'valueprop', name: 'Value', type: 'number', format: 'currency:USD' },
      { id: 'ownerprop', name: 'Owner', type: 'person' },
      { id: 'dateprop', name: 'Due', type: 'date' },
    ];
    const rows: MdDatabaseRow[] = Array.from({ length: MAX_ROWS }, (_, index) => ({
      id: `scale${String(index + 1).padStart(6, '0')}`,
      values: { title: `Work item ${index + 1}`, stageprop: stages[index % stages.length]!.id,
        valueprop: 1000 + (index * 7919) % 99000, ownerprop: [`Owner ${index % 32 + 1}`],
        dateprop: new Date(now + (index % 365) * day).toISOString().slice(0, 10) },
      body: '', comments: [], created: now - (index % 720) * day, updated: now - (index % 60) * day,
      createdBy: 'QA', updatedBy: 'QA',
    }));
    const base = { visiblePropertyIds: properties.map((property) => property.id), sorts: [] as MdDatabaseSort[],
      filters: [] as MdDatabaseFilter[], groupBy: null, groupDirection: 'asc' as const, hiddenGroups: [] as string[],
      calculations: { title: 'count', valueprop: 'sum' } as Record<string, MdDatabaseCalculation>, datePropertyId: 'dateprop',
      endDatePropertyId: null, chartPropertyId: 'valueprop', chartType: 'bar' as const, chartAggregation: 'sum' as const,
      propertyWidths: { title: 250, stageprop: 140, valueprop: 150, ownerprop: 180, dateprop: 140 }, frozenPropertyIds: ['title'],
      wrapPropertyIds: [] as string[], showDataSourceTitle: true, showVerticalLines: true, showPageIcon: true,
      wrapContent: false, openPagesIn: 'side_peek' as const, conditionalColors: [] as MdDatabaseView['conditionalColors'] };
    const views: MdDatabaseView[] = [
      { ...base, id: 'scale-table', name: 'All 5,000', type: 'table', sorts: [{ propertyId: 'dateprop', direction: 'asc' }] },
      { ...base, id: 'scale-board', name: 'Board', type: 'board', groupBy: 'stageprop' },
      { ...base, id: 'scale-gallery', name: 'Gallery', type: 'gallery' },
      { ...base, id: 'scale-list', name: 'List', type: 'list' },
      { ...base, id: 'scale-feed', name: 'Feed', type: 'feed' },
    ];
    const saved = mdDatabaseSave({ version: VERSION, id: databaseId, name: 'Scale Laboratory · 5,000 rows',
      properties, rows, views, activeViewId: views[0]!.id, locked: false, templates: [], automations: [],
      created: now, updated: now, revision: 1 });
    return saved ? { databaseId, rows: saved.rows.length, line: mdDatabaseLine(databaseId) } : null;
  }

  const api = { MD_DATABASE_TYPES: TYPES.filter((type) => type !== 'title'), MD_DATABASE_VIEW_TYPES: VIEW_TYPES,
    MD_DATABASE_CALCULATIONS: CALCULATIONS, MD_DATABASE_FILTER_OPERATORS: FILTERS,
    mdDatabaseSanitise, mdDatabaseSanitiseSyncDoc, mdDatabaseRowSanitise, mdViewDocSanitise, mdFormDocSanitise,
    mdFormRead, mdFormList, mdFormSave, mdFormCreate, mdFormUpdate, mdFormRemove, mdFormSubmit,
    mdDatabaseRead, mdDatabaseCacheReset, mdDatabaseForgetAll, mdDatabaseList, mdDatabaseBuiltin, mdDatabasePickerRows, mdDatabaseRowFind, mdDatabaseSave, mdDatabaseCreate,
    mdDatabaseRename, mdDatabaseTryRename, mdDatabaseSetScope, mdDatabasePropertyAdd, mdDatabasePropertyUpdate, mdDatabasePropertyRename,
    mdDatabasePropertySetType, mdDatabasePropertyDuplicate, mdDatabasePropertyRemove,
    mdDatabasePropertyMove, mdDatabasePropertyInsert, mdDatabaseViewPropertyMove,
    mdDatabaseOptionAdd, mdDatabaseOptionUpdate, mdDatabaseOptionRemove,
    mdDatabaseRowAdd, mdDatabaseTryRowAdd, mdDatabaseRowMove, mdDatabaseRowMoveTo, mdDatabaseRowMetaUpdate, mdDatabaseRowCommentAdd, mdDatabaseRowCommentRemove,
    mdDatabaseCellSet, mdDatabaseTryCellSet, mdDatabaseTryCellsSet, mdDatabaseTryMemberAdd, mdDatabaseRelationMirror, mdDatabaseRowBodySet, mdDatabaseRowRemove, mdDatabaseTryRowRemove, mdDatabaseTryRowsRemove, mdDatabaseTryRowsDuplicate, mdDatabaseRowDuplicate,
    mdDatabaseValue, mdDatabaseCalculate, mdDatabaseButtonRun, mdDatabaseViewAdd, mdDatabaseViewUpdate, mdDatabaseViewActivate,
    mdDatabaseViewRemove, mdDatabaseViewDuplicate, mdDatabaseRowsForView, mdDatabaseRowCount, mdDatabaseGroups, mdDatabaseDelete,
    mdDatabaseLock, mdDatabaseTemplateAdd, mdDatabaseTemplateUpdate, mdDatabaseTemplateRemove, mdDatabaseRowAddFromTemplate,
    mdDatabaseAutomationAdd, mdDatabaseAutomationUpdate, mdDatabaseAutomationRemove, mdDatabaseDemoCreate, mdDatabaseScaleCreate,
    mdDatabaseHistoryState, mdDatabaseUndo, mdDatabaseRedo,
    mdDatabaseLine, mdDatabaseIdFromLine, mdDatabaseBlockOf, mdDatabaseSyncDocs, mdDatabaseApplyDoc,
    mdDatabaseMergesDoc };
  Object.assign(g, api);
  if (typeof g.location?.search === 'string' && new URLSearchParams(g.location.search).get('qa') === 'db500') {
    mdDatabaseDemoCreate(500);
  }
  if (typeof g.location?.search === 'string' && new URLSearchParams(g.location.search).get('qa') === 'db5k') {
    mdDatabaseScaleCreate();
  }
  const mod = (globalThis as { module?: { exports?: unknown } }).module; if (mod && mod.exports) mod.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
